import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router';
import {
  LineChart, Line, AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip as ChartTooltip,
  Legend, ReferenceLine, ResponsiveContainer, BarChart, Bar, Cell,
} from 'recharts';
import {
  hasPhpBackend, apiListReports, apiDeleteReport, apiSaveReport,
  apiListInfluentSamples, apiListCustomerSamples,
  apiListSAWaterEstimates, apiSaveSAWaterEstimate,
  apiListCustomerSites, hasAuthToken,
} from '@/lib/phpApi';
import { useAuth } from '@/hooks/useAuth';
import { useSession } from '@/context/SessionContext';
import {
  generateSampleTrendReportPDF,
  generateSAWaterChargesReport,
  buildRegulatoryReportNumber,
} from '@/lib/pdfGenerator';
import type { SAWaterChargeRow } from '@/lib/pdfGenerator';
import { ALL_STANDARDS } from '@/types/standards';
import PageHeader from '@/components/PageHeader';
import { toast } from 'sonner';
import {
  FileText, Trash2, Download, Eye, Search, X, ChevronDown, ChevronRight,
  Database, HardDrive, Calendar, Layers, Building2, MapPin,
  Shield, Hash, Upload, RefreshCw, Beaker, BarChart3, AlertTriangle,
  CheckCircle2, Info, Receipt, Plus, TrendingUp, Droplets, Loader2,
  Activity, DollarSign, CheckCircle, Filter, Gauge, Save, TrendingDown,
  SlidersHorizontal, ArrowUpRight, Zap, BarChart2,
} from 'lucide-react';

// ─── Types ────────────────────────────────────────────────────────────────────
interface ApiSite {
  id: number;
  name: string;
  address?: string;
  postcode?: string;
  standard?: string;
  permitRef?: string;
  permit_ref?: string;
}
interface ApiSample {
  id: number;
  sampleName?: string;
  sample_name?: string;
  reportNumber?: string | null;
  report_number?: string | null;
  siteId?: number | string | null;
  site_id?: number | string | null;
  createdAt?: string;
  created_at?: string;
  sample_date?: string;
  sampleType?: string;
  sample_type?: string;
  params?: unknown;
}

// ─── SA Water Rates ───────────────────────────────────────────────────────────
const SA_WATER_RATES = [
  { key: 'bod5', label: 'BOD₅', fullName: 'Biochemical Oxygen Demand', lowRate: 0.251, highRate: 0.378, threshold: 1000, triggerKg: 10000, tknCondition: false, color: '#f97316' },
  { key: 'tss',  label: 'SS',   fullName: 'Suspended Solids',          lowRate: 0.218, highRate: 0.315, threshold: 500,  triggerKg: 10000, tknCondition: false, color: '#eab308' },
  { key: 'tds',  label: 'TDS',  fullName: 'Total Dissolved Solids',    lowRate: 0.126, highRate: 0.126, threshold: null, triggerKg: 20000, tknCondition: false, color: '#3b82f6' },
  { key: 'tn',   label: 'TKN',  fullName: 'Total Kjeldahl Nitrogen',   lowRate: 0.392, highRate: 0.392, threshold: null, triggerKg: null,  tknCondition: true,  color: '#8b5cf6' },
  { key: 'totalPhosphorus', label: 'TP', fullName: 'Total Phosphorus', lowRate: 1.908, highRate: 1.908, threshold: null, triggerKg: null,  tknCondition: false, color: '#ec4899' },
] as const;

const SA_WATER_PERMIT: Record<string, number> = {
  bod5: 3000, tss: 500, tds: 1500, tn: 130, totalPhosphorus: 15,
  ph_high: 10, ph_low: 6, temperature: 38, oilGrease: 100,
};

function calcSAWaterCharges(params: Record<string, number>, annualKL: number) {
  const volumeTriggered = annualKL >= 10000;
  function extract(key: string): number | null {
    const aliases: Record<string, string[]> = {
      bod5: ['bod5','bod','BOD5','BOD'], tss: ['tss','TSS','ss','SS'],
      tds: ['tds','TDS'], tn: ['tn','TN','tkn','TKN','totalNitrogen'],
      totalPhosphorus: ['totalPhosphorus','tp','TP','phosphorus'],
    };
    for (const k of (aliases[key] ?? [key])) { const v = params[k]; if (typeof v === 'number' && v >= 0) return v; }
    return null;
  }
  return SA_WATER_RATES.map(r => {
    const avg = extract(r.key);
    const loadKg = avg !== null && annualKL > 0 ? avg * annualKL * 0.001 : null;
    const highRateApplied = r.threshold !== null && avg !== null && avg > r.threshold;
    const rate = highRateApplied ? r.highRate : r.lowRate;
    const triggered = volumeTriggered || (r.triggerKg !== null && loadKg !== null && loadKg > r.triggerKg);
    const exceeds = r.tknCondition ? (avg !== null && avg > 650) : true;
    const charge = triggered && exceeds && loadKg !== null ? loadKg * rate : null;
    return { key: r.key, label: r.label, fullName: r.fullName, color: r.color, avg, loadKg, rate, charge, triggered, exceeds, highRateApplied };
  });
}

// ─── Utilities ────────────────────────────────────────────────────────────────
function parseDate(d: string | null | undefined): Date | null {
  if (!d) return null;
  try { const iso = String(d).replace(' ', 'T'); const dt = new Date(iso); return isNaN(dt.getTime()) ? null : dt; } catch { return null; }
}
function fmtDate(d: string | null | undefined) {
  const dt = parseDate(d); if (!dt) return '—';
  return dt.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
}
function fmtDateShort(d: string | null | undefined) {
  const dt = parseDate(d); if (!dt) return '—';
  return dt.toLocaleDateString('en-GB', { day: '2-digit', month: 'short' });
}
function fmtDateFull(d: string | null | undefined) {
  const dt = parseDate(d); if (!dt) return '—';
  return dt.toLocaleDateString('en-GB', { day: '2-digit', month: 'long', year: 'numeric' });
}
function fmtAUD(n: number) {
  return '$' + n.toLocaleString('en-AU', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}
function fmtNum(n: number, dp = 0) {
  return n.toLocaleString('en-AU', { minimumFractionDigits: dp, maximumFractionDigits: dp });
}
function linearRegression(xs: number[], ys: number[]): { slope: number; intercept: number } {
  const n = xs.length;
  if (n < 2) return { slope: 0, intercept: ys[0] ?? 0 };
  const sumX  = xs.reduce((a, b) => a + b, 0);
  const sumY  = ys.reduce((a, b) => a + b, 0);
  const sumXY = xs.reduce((s, xi, i) => s + xi * ys[i], 0);
  const sumX2 = xs.reduce((s, xi) => s + xi * xi, 0);
  const denom = n * sumX2 - sumX * sumX;
  if (denom === 0) return { slope: 0, intercept: sumY / n };
  const slope     = (n * sumXY - sumX * sumY) / denom;
  const intercept = (sumY - slope * sumX) / n;
  return { slope, intercept };
}

function stdBadgeClass(std: string) {
  if (std === 'UK_EA')    return 'bg-blue-500/10 text-blue-400 border-blue-500/20';
  if (std === 'AU_EPA')   return 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20';
  if (std === 'SA_WATER') return 'bg-orange-500/10 text-orange-400 border-orange-500/20';
  if (std === 'EU_UWWTD') return 'bg-violet-500/10 text-violet-400 border-violet-500/20';
  if (std === 'US_EPA')   return 'bg-amber-500/10 text-amber-400 border-amber-500/20';
  return 'bg-slate-700 text-slate-400 border-slate-600';
}
function parseParams(raw: unknown): Record<string, number> {
  if (!raw) return {};
  const obj = typeof raw === 'string' ? (() => { try { return JSON.parse(raw); } catch { return {}; } })() : raw;
  return obj as Record<string, number>;
}
function extractValue(params: Record<string, number>, key: string): number | null {
  const aliases: Record<string, string[]> = {
    bod5: ['bod5','bod','BOD5','BOD'], tss: ['tss','TSS','ss','SS'],
    tds: ['tds','TDS'], tn: ['tn','TN','tkn','TKN','totalNitrogen'],
    totalPhosphorus: ['totalPhosphorus','tp','TP','phosphorus'],
    ph: ['ph','pH'], temperature: ['temperature','temp'],
    oilGrease: ['oilGrease','oilgrease','og','OG'],
  };
  for (const k of (aliases[key] ?? [key])) { const v = params[k]; if (typeof v === 'number' && v >= 0) return v; }
  return null;
}
const PARAM_LABELS: Record<string, string> = {
  ph: 'pH', bod: 'BOD₅', bod5: 'BOD₅', cod: 'COD', tss: 'TSS', nh3: 'NH₃-N',
  og: 'O&G', oandg: 'O&G', temperature: 'Temp', tds: 'TDS',
  conductivity: 'Cond.', totalNitrogen: 'TN', totalPhosphorus: 'TP', tn: 'TKN',
};
const PARAM_UNITS: Record<string, string> = {
  ph: '', bod: 'mg/L', bod5: 'mg/L', cod: 'mg/L', tss: 'mg/L', nh3: 'mg/L',
  og: 'mg/L', temperature: '°C', tds: 'mg/L', conductivity: 'µS/cm',
  totalNitrogen: 'mg/L', totalPhosphorus: 'mg/L', tn: 'mg/L',
};
function normalizeReportNum(rn: string | null | undefined, standard: string): string {
  if (rn && rn.includes('/')) return rn;
  return buildRegulatoryReportNumber('EFF', standard);
}
function paramColour(key: string, value: number, standard: string): 'red' | 'amber' | 'green' | 'neutral' {
  const std = ALL_STANDARDS.find(s => s.code === standard);
  if (!std) return 'neutral';
  const limit = (std.limits as Record<string, unknown>)[key.toLowerCase()];
  if (typeof limit !== 'number' || limit <= 0) return 'neutral';
  const pct = value / limit;
  if (pct > 1) return 'red';
  if (pct > 0.8) return 'amber';
  return 'green';
}
const COLOUR_CLASSES: Record<string, string> = {
  red:     'bg-red-500/15 text-red-400 border-red-500/30',
  amber:   'bg-amber-500/15 text-amber-400 border-amber-500/30',
  green:   'bg-emerald-500/15 text-emerald-400 border-emerald-500/30',
  neutral: 'bg-slate-700/50 text-slate-400 border-slate-600/50',
};

// ─── Sparkline ────────────────────────────────────────────────────────────────
function Sparkline({ values, limit }: { values: number[]; limit?: number }) {
  if (values.length < 2) return null;
  const max = Math.max(...values, limit || 0) * 1.1 || 1;
  const W = 80; const H = 24;
  const pts = values.map((v, i) => `${(i / (values.length - 1)) * W},${H - (v / max) * H}`).join(' ');
  const limitY = limit ? H - (limit / max) * H : null;
  return (
    <svg width={W} height={H} className="block">
      {limitY !== null && <line x1={0} y1={limitY} x2={W} y2={limitY} stroke="#ef4444" strokeWidth="1" strokeDasharray="3,2" opacity="0.6" />}
      <polyline points={pts} fill="none" stroke="var(--rb-brand)" strokeWidth="1.5" />
      {values.map((v, i) => {
        const x = (i / (values.length - 1)) * W;
        const y = H - (v / max) * H;
        const c = limit ? (v > limit ? '#ef4444' : v > limit * 0.8 ? '#f59e0b' : '#10b981') : 'var(--rb-brand)';
        return <circle key={i} cx={x} cy={y} r="2.5" fill={c} />;
      })}
    </svg>
  );
}

// ─── Trend Chart Tooltip ──────────────────────────────────────────────────────
function TrendTooltip({ active, payload, label }: { active?: boolean; payload?: any[]; label?: string }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-slate-800 border border-slate-600/60 rounded-xl shadow-xl px-3 py-2.5 text-xs max-w-[220px]">
      <p className="text-[10px] font-bold text-slate-300 mb-1.5">{label}</p>
      {payload.map((p: any) => (
        <div key={p.dataKey} className="flex items-center justify-between gap-3">
          <span className="text-[10px] text-slate-400">{p.name}</span>
          <span className="text-[10px] font-bold font-mono" style={{ color: p.color }}>{typeof p.value === 'number' ? p.value.toFixed(1) : '—'}</span>
        </div>
      ))}
    </div>
  );
}

// ─── SA Water Estimate Row ────────────────────────────────────────────────────
function SAWaterEstimateRow({ est, customerName }: { est: any; customerName: string | null }) {
  const [open, setOpen] = useState(false);
  const data   = (est.estimate_data as any) || {};
  const total  = Number(est.total_charge);
  const incGST = total * 1.1;
  const hasCharges = total > 0;
  const chargeRows: any[] = data.chargeRows || [];
  const CHARGE_PARAM_ORDER = ['BOD₅', 'SS', 'TDS', 'TKN', 'TP'];
  const orderedRows = CHARGE_PARAM_ORDER
    .map(label => chargeRows.find((r: any) => r.label === label) || null)
    .filter(Boolean) as any[];
  chargeRows.forEach((r: any) => { if (!CHARGE_PARAM_ORDER.includes(r.label)) orderedRows.push(r); });

  const handleDownloadPDF = useCallback(() => {
    const rows: SAWaterChargeRow[] = chargeRows.map((r: any) => ({
      label: r.label, fullName: r.fullName, avg: r.avg ?? null, loadKg: r.loadKg ?? null,
      rate: r.rate ?? 0, charge: r.charge ?? null, triggered: !!r.triggered,
      exceeds: !!r.exceeds, highRateApplied: !!r.highRateApplied,
    }));
    generateSAWaterChargesReport({
      customerCompany: data.customerName || customerName || 'Unknown',
      siteName: data.siteName || 'Unknown Site',
      siteAddress: data.siteAddress || '',
      sitePostcode: data.sitePostcode || undefined,
      permitRef: data.permitRef || undefined,
      sampleLabel: est.sample_label || `${est.sample_count || '?'} samples`,
      annualKL: Number(est.annual_kl) || 0,
      flowRateM3day: est.flow_m3h ? Number(est.flow_m3h) * 24 : undefined,
      operatingDays: Number(est.operating_days) || 250,
      volumeMethod: 'flowrate',
      rows,
      totalCharge: Number(est.total_charge),
      referenceNumber: est.reference_number,
      preparedBy: data.preparedBy || undefined,
      mode: 'download',
    });
    toast.success(`Downloaded: ${est.reference_number}`);
  }, [est, data, chargeRows, customerName]);

  return (
    <div className="border border-slate-800/40 rounded-xl overflow-hidden">
      <button className="w-full flex items-center gap-3 px-4 py-3 hover:bg-slate-800/20 transition-colors text-left" onClick={() => setOpen(o => !o)}>
        <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 border ${hasCharges ? 'bg-red-500/10 border-red-500/20' : 'bg-emerald-500/10 border-emerald-500/20'}`}>
          <Receipt className={`w-3.5 h-3.5 ${hasCharges ? 'text-red-400' : 'text-emerald-400'}`} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap mb-0.5">
            <span className="text-[11px] font-mono font-bold text-orange-300">{est.reference_number}</span>
            <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-bold border ${hasCharges ? 'bg-red-500/10 text-red-400 border-red-500/20' : 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20'}`}>
              {hasCharges ? `${fmtAUD(total)}/yr` : 'No excess charges'}
            </span>
          </div>
          <div className="flex items-center gap-3 flex-wrap">
            <span className="text-[10px] text-slate-500 flex items-center gap-1"><Calendar className="w-2.5 h-2.5" />{fmtDate(est.created_at)}</span>
            {est.sample_label && <span className="text-[10px] text-slate-500">{est.sample_label}</span>}
            <span className="text-[10px] text-slate-600">{fmtNum(Number(est.annual_kl))} kL/yr</span>
          </div>
        </div>
        <ChevronDown className={`w-3.5 h-3.5 text-slate-500 shrink-0 transition-transform ${open ? 'rotate-180 text-orange-400' : ''}`} />
      </button>
      <AnimatePresence>
        {open && (
          <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.2 }} className="overflow-hidden">
            <div className="border-t border-slate-800/40 px-4 pt-4 pb-5 space-y-4">
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5">
                {[
                  { label: 'Annual Volume', value: `${fmtNum(Number(est.annual_kl))} kL` },
                  { label: 'Flow Rate', value: est.flow_m3h ? `${Number(est.flow_m3h).toFixed(1)} m³/h` : '—' },
                  { label: 'Operating Days', value: `${est.operating_days || 250} d/yr` },
                  { label: 'Total Charge', value: fmtAUD(total), bold: true, colour: hasCharges ? 'text-red-400' : 'text-emerald-400' },
                ].map(({ label, value, bold, colour }) => (
                  <div key={label} className="bg-slate-800/40 border border-slate-800/40 rounded-lg p-2.5">
                    <p className="text-[10px] text-slate-500 mb-1">{label}</p>
                    <p className={`text-[11px] ${bold ? 'font-black' : 'font-semibold'} ${colour || 'text-white'}`}>{value}</p>
                  </div>
                ))}
              </div>
              {hasCharges && (
                <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-red-500/5 border border-red-500/15">
                  <DollarSign className="w-3.5 h-3.5 text-red-400 shrink-0" />
                  <span className="text-[10px] text-slate-400">Incl. GST:</span>
                  <span className="text-[11px] font-black text-red-300">{fmtAUD(incGST)}/yr</span>
                </div>
              )}
              {orderedRows.length > 0 && (
                <div className="overflow-x-auto rounded-lg border border-orange-500/15">
                  <table className="w-full text-[11px]">
                    <thead>
                      <tr className="bg-orange-500/5 border-b border-orange-500/15">
                        <th className="text-left px-3 py-2 text-slate-400 font-semibold whitespace-nowrap">Parameter</th>
                        <th className="text-right px-3 py-2 text-slate-400 font-semibold whitespace-nowrap">Avg mg/L</th>
                        <th className="text-right px-3 py-2 text-slate-400 font-semibold whitespace-nowrap">Load kg/yr</th>
                        <th className="text-right px-3 py-2 text-slate-400 font-semibold whitespace-nowrap">Rate $/kg</th>
                        <th className="text-right px-3 py-2 text-slate-400 font-semibold whitespace-nowrap">Annual Charge</th>
                        <th className="text-left px-3 py-2 text-slate-400 font-semibold whitespace-nowrap">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {orderedRows.map((row: any, i: number) => {
                        const charged = typeof row.charge === 'number' && row.charge > 0;
                        const avgNull = row.avg === null || row.avg === undefined;
                        let chargeCell: React.ReactNode;
                        let statusCell: React.ReactNode;
                        if (avgNull) {
                          chargeCell = <span className="text-slate-600">—</span>;
                          statusCell = <span className="text-[10px] text-slate-600">No data</span>;
                        } else if (!row.triggered) {
                          chargeCell = <span className="text-emerald-400 font-semibold">Below trigger</span>;
                          statusCell = <span className="inline-flex items-center gap-1 text-[10px] text-emerald-400"><CheckCircle className="w-3 h-3" /> OK</span>;
                        } else if (row.label === 'TKN' && !row.exceeds) {
                          chargeCell = <span className="text-amber-400 font-semibold">≤ 650 mg/L</span>;
                          statusCell = <span className="text-[10px] text-amber-400">Within limit</span>;
                        } else if (charged) {
                          chargeCell = <span className="font-black text-red-400">{fmtAUD(row.charge)}</span>;
                          statusCell = <span className="inline-flex items-center gap-1 text-[10px] text-red-400"><AlertTriangle className="w-3 h-3" /> Charged{row.highRateApplied ? ' (high rate)' : ''}</span>;
                        } else {
                          chargeCell = <span className="text-emerald-400 font-semibold">No charge</span>;
                          statusCell = <span className="inline-flex items-center gap-1 text-[10px] text-emerald-400"><CheckCircle className="w-3 h-3" /> OK</span>;
                        }
                        return (
                          <tr key={row.label || i} className={`border-b border-slate-800/50 last:border-0 ${charged ? 'bg-red-500/5' : ''}`}>
                            <td className="px-3 py-2.5"><span className="font-bold text-slate-200">{row.label}</span>{row.fullName && <span className="text-[10px] text-slate-500 ml-1.5">{row.fullName}</span>}</td>
                            <td className="px-3 py-2.5 text-right text-slate-300">{avgNull ? '—' : Number(row.avg).toFixed(1)}</td>
                            <td className="px-3 py-2.5 text-right text-slate-300">{row.loadKg != null ? fmtNum(Number(row.loadKg), 1) : '—'}</td>
                            <td className="px-3 py-2.5 text-right text-slate-400">{row.rate != null ? `$${Number(row.rate).toFixed(2)}` : '—'}</td>
                            <td className="px-3 py-2.5 text-right">{chargeCell}</td>
                            <td className="px-3 py-2.5">{statusCell}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                    {hasCharges && (
                      <tfoot>
                        <tr className="bg-orange-500/5 border-t border-orange-500/15">
                          <td colSpan={4} className="px-3 py-2 text-right text-[10px] font-bold text-slate-400 uppercase tracking-wider">Total Annual Charge</td>
                          <td className="px-3 py-2 text-right font-black text-red-400">{fmtAUD(total)}</td>
                          <td className="px-3 py-2" />
                        </tr>
                      </tfoot>
                    )}
                  </table>
                </div>
              )}
              <div className="flex justify-end pt-1">
                <button onClick={handleDownloadPDF} className="flex items-center gap-1.5 px-4 py-2 rounded-xl text-[11px] font-bold bg-orange-500/10 text-orange-400 border border-orange-500/20 hover:bg-orange-500/20 transition-all">
                  <Download className="w-3.5 h-3.5" /> Download PDF Report
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

// ─── Inline SA Water Calculator for a single sample ──────────────────────────
function InlineSAWaterCalc({
  sample, site, customerName, preparedBy,
  onSaved,
}: {
  sample: ApiSample;
  site: ApiSite;
  customerName: string | null;
  preparedBy?: string;
  onSaved: () => void;
}) {
  const [flow, setFlow] = useState(5);
  const [days, setDays] = useState(250);
  const [saving, setSaving] = useState(false);

  const params = useMemo(() => parseParams(sample.params), [sample.params]);
  const annualKL = flow * 24 * days;
  const chargeRows = useMemo(() => calcSAWaterCharges(params, annualKL), [params, annualKL]);
  const totalCharge = chargeRows.reduce((s, r) => s + (r.charge ?? 0), 0);
  const anyCharged = chargeRows.some(r => r.charge !== null && r.charge > 0);
  const sampleLabel = sample.reportNumber || sample.report_number || sample.sampleName || sample.sample_name || `Sample #${sample.id}`;
  const permitRef = site.permitRef || site.permit_ref;

  const handleSave = async () => {
    setSaving(true);
    try {
      const refNum = buildRegulatoryReportNumber('EFF', 'SA_WATER');
      const customerId = sample.siteId ?? sample.site_id;
      await apiSaveSAWaterEstimate({
        customer_id: Number(customerId) || 0,
        site_id: site.id,
        reference_number: refNum,
        sample_count: 1,
        annual_kl: annualKL,
        flow_m3h: flow,
        operating_days: days,
        total_charge: totalCharge,
        sample_label: `Single sample — ${sampleLabel}`,
        estimate_data: {
          customerName: customerName || '',
          siteName: site.name,
          siteAddress: site.address || '',
          sitePostcode: site.postcode || '',
          permitRef: permitRef || '',
          preparedBy: preparedBy || '',
          volumeMethod: 'flowrate',
          chargeRows: chargeRows.map(r => ({
            label: r.label, fullName: r.fullName, avg: r.avg, loadKg: r.loadKg,
            rate: r.rate, charge: r.charge, triggered: r.triggered,
            exceeds: r.exceeds, highRateApplied: r.highRateApplied,
          })),
        } as Record<string, unknown>,
      });
      toast.success(`Estimate saved — ${refNum}`);
      onSaved();
    } catch (e: any) {
      toast.error(e.message || 'Failed to save estimate');
    } finally {
      setSaving(false);
    }
  };

  const handleDownloadPDF = () => {
    const ref = buildRegulatoryReportNumber('EFF', 'SA_WATER');
    const pdfRows: SAWaterChargeRow[] = chargeRows.map(r => ({
      label: r.label, fullName: r.fullName, avg: r.avg, loadKg: r.loadKg,
      rate: r.rate, charge: r.charge, triggered: r.triggered,
      exceeds: r.exceeds, highRateApplied: r.highRateApplied,
    }));
    generateSAWaterChargesReport({
      customerCompany: customerName || 'Unknown',
      siteName: site.name,
      siteAddress: site.address || '',
      sitePostcode: site.postcode,
      permitRef,
      sampleLabel: `Single sample — ${sampleLabel}`,
      annualKL,
      flowRateM3day: flow * 24,
      operatingDays: days,
      volumeMethod: 'flowrate',
      rows: pdfRows,
      totalCharge,
      referenceNumber: ref,
      preparedBy,
      mode: 'download',
    });
  };

  return (
    <div className="mt-3 p-3 rounded-xl border border-orange-500/20 space-y-4"
      style={{ background: 'linear-gradient(135deg, rgba(249,115,22,0.04) 0%, rgba(8,14,28,0.9) 60%)' }}>

      {/* Header summary */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="w-6 h-6 rounded-lg bg-orange-500/15 border border-orange-500/25 flex items-center justify-center">
            <Gauge className="w-3 h-3 text-orange-400" />
          </div>
          <span className="text-[11px] font-bold text-orange-300">SA Water Charge Estimate</span>
        </div>
        {anyCharged && (
          <span className="text-[11px] font-black text-red-400 px-2 py-0.5 rounded-lg bg-red-500/10 border border-red-500/20">
            {fmtAUD(totalCharge)}/yr est.
          </span>
        )}
        {!anyCharged && chargeRows.some(r => r.avg !== null) && (
          <span className="text-[11px] font-bold text-emerald-400 px-2 py-0.5 rounded-lg bg-emerald-500/10 border border-emerald-500/20">
            ✓ No charges
          </span>
        )}
      </div>

      {/* Inputs — flow slider + days */}
      <div className="space-y-3">
        {/* Flow rate with slider */}
        <div>
          <div className="flex items-center justify-between mb-1.5">
            <label className="text-[10px] text-slate-500 font-bold uppercase tracking-wider flex items-center gap-1">
              <SlidersHorizontal className="w-3 h-3" /> Flow Rate
            </label>
            <div className="flex items-center gap-1.5">
              <input type="number" value={flow} min={0.1} max={500} step={0.5}
                onChange={e => setFlow(Math.max(0.1, parseFloat(e.target.value) || 0.1))}
                className="bg-slate-800/60 border border-slate-600/50 rounded-lg text-white text-[11px] px-2 py-1 w-20 text-right focus:outline-none focus:border-orange-400/40"
              />
              <span className="text-[10px] text-slate-500 font-bold">m³/h</span>
            </div>
          </div>
          <input
            type="range" min={0.5} max={200} step={0.5} value={Math.min(flow, 200)}
            onChange={e => setFlow(parseFloat(e.target.value))}
            className="w-full h-1.5 rounded-full appearance-none cursor-pointer"
            style={{
              background: `linear-gradient(to right, #f97316 0%, #f97316 ${(Math.min(flow,200)/200)*100}%, rgba(100,116,139,0.3) ${(Math.min(flow,200)/200)*100}%, rgba(100,116,139,0.3) 100%)`,
              accentColor: '#f97316',
            }}
          />
          <div className="flex justify-between text-[9px] text-slate-400 mt-0.5">
            <span>0.5</span><span>50</span><span>100</span><span>150</span><span>200 m³/h</span>
          </div>
        </div>

        {/* Operating days */}
        <div className="flex items-center gap-3">
          <div className="flex-1">
            <div className="flex items-center justify-between mb-1.5">
              <label className="text-[10px] text-slate-500 font-bold uppercase tracking-wider">Operating Days/yr</label>
              <div className="flex items-center gap-1.5">
                <input type="number" value={days} min={1} max={365} step={1}
                  onChange={e => setDays(Math.min(365, Math.max(1, parseInt(e.target.value) || 1)))}
                  className="bg-slate-800/60 border border-slate-600/50 rounded-lg text-white text-[11px] px-2 py-1 w-20 text-right focus:outline-none focus:border-orange-400/40"
                />
                <span className="text-[10px] text-slate-500 font-bold">days</span>
              </div>
            </div>
            <input
              type="range" min={1} max={365} step={1} value={days}
              onChange={e => setDays(parseInt(e.target.value))}
              className="w-full h-1.5 rounded-full appearance-none cursor-pointer"
              style={{
                background: `linear-gradient(to right, #8b5cf6 0%, #8b5cf6 ${(days/365)*100}%, rgba(100,116,139,0.3) ${(days/365)*100}%, rgba(100,116,139,0.3) 100%)`,
                accentColor: '#8b5cf6',
              }}
            />
          </div>
        </div>

        {/* Annual kL summary */}
        <div className="flex items-center gap-3 px-3 py-2 rounded-lg bg-slate-800/50 border border-slate-800/40">
          <Droplets className="w-3.5 h-3.5 text-blue-400 shrink-0" />
          <span className="text-[10px] text-slate-500">Annual volume:</span>
          <span className="font-black text-blue-400 text-[13px]">{fmtNum(annualKL)} kL/yr</span>
          {annualKL >= 10000 && (
            <span className="ml-auto text-[10px] text-red-400 font-bold flex items-center gap-1">
              <AlertTriangle className="w-3 h-3" /> Volume trigger active
            </span>
          )}
        </div>
      </div>

      {/* Charge table */}
      <div className="overflow-x-auto rounded-lg border border-slate-800/40">
        <table className="w-full text-[11px]">
          <thead>
            <tr className="bg-slate-800/60 border-b border-slate-800/40">
              <th className="text-left px-3 py-2 text-slate-400 font-semibold whitespace-nowrap">Parameter</th>
              <th className="text-right px-2 py-2 text-slate-400 font-semibold whitespace-nowrap">Avg mg/L</th>
              <th className="text-right px-2 py-2 text-slate-400 font-semibold whitespace-nowrap">Load kg/yr</th>
              <th className="text-right px-2 py-2 text-slate-400 font-semibold whitespace-nowrap">Rate $/kg</th>
              <th className="text-right px-3 py-2 text-slate-400 font-semibold whitespace-nowrap">Charge/yr</th>
              <th className="text-left px-2 py-2 text-slate-400 font-semibold whitespace-nowrap">Status</th>
            </tr>
          </thead>
          <tbody>
            {chargeRows.map(r => {
              const noData = r.avg === null;
              const charged = r.charge !== null && r.charge > 0;
              return (
                <tr key={r.key} className={`border-b border-slate-800/50 last:border-0 ${charged ? 'bg-red-500/5' : ''}`}>
                  <td className="px-3 py-2">
                    <div className="flex items-center gap-1.5">
                      <div className="w-1.5 h-1.5 rounded-full shrink-0" style={{ backgroundColor: r.color }} />
                      <span className="font-bold text-slate-200">{r.label}</span>
                    </div>
                  </td>
                  <td className="px-2 py-2 text-right font-mono text-slate-300">{noData ? '—' : r.avg!.toFixed(1)}</td>
                  <td className="px-2 py-2 text-right font-mono text-slate-400">{r.loadKg != null ? fmtNum(r.loadKg, 1) : '—'}</td>
                  <td className="px-2 py-2 text-right font-mono text-slate-500">{noData ? '—' : `$${r.rate.toFixed(3)}`}</td>
                  <td className="px-3 py-2 text-right font-mono font-black">
                    {noData ? <span className="text-slate-600">—</span>
                      : !r.triggered ? <span className="text-emerald-400 text-[10px] font-bold">Below trigger</span>
                      : r.label === 'TKN' && !r.exceeds ? <span className="text-amber-400 text-[10px]">≤ 650</span>
                      : charged ? <span className="text-red-400">{fmtAUD(r.charge!)}</span>
                      : <span className="text-emerald-400 text-[10px]">No charge</span>
                    }
                  </td>
                  <td className="px-2 py-2">
                    {charged
                      ? <span className="text-[10px] text-red-400 font-bold">⚠ Charged</span>
                      : noData ? <span className="text-[10px] text-slate-600">No data</span>
                      : <span className="text-[10px] text-emerald-400">✓ OK</span>
                    }
                  </td>
                </tr>
              );
            })}
          </tbody>
          <tfoot>
            <tr className="bg-orange-500/5 border-t border-orange-500/20">
              <td colSpan={4} className="px-3 py-2 text-right text-[10px] font-bold text-slate-400 uppercase">Total Annual Charge</td>
              <td className={`px-3 py-2 text-right font-black font-mono ${anyCharged ? 'text-red-400' : 'text-emerald-400'}`}>
                {fmtAUD(totalCharge)}
              </td>
              <td className="px-2 py-2 text-[10px] text-slate-500">excl. GST</td>
            </tr>
            {anyCharged && (
              <tr className="bg-orange-500/3 border-t border-slate-800/50">
                <td colSpan={4} className="px-3 py-1.5 text-right text-[10px] text-slate-500">Incl. GST</td>
                <td className="px-3 py-1.5 text-right font-black font-mono text-red-300">{fmtAUD(totalCharge * 1.1)}</td>
                <td />
              </tr>
            )}
          </tfoot>
        </table>
      </div>

      <div className="flex items-center gap-2 pt-1">
        <button onClick={handleSave} disabled={saving}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[11px] font-bold bg-orange-500/15 text-orange-400 border border-orange-500/25 hover:bg-orange-500/25 disabled:opacity-50 transition-all">
          {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />}
          Save Estimate
        </button>
        <button onClick={handleDownloadPDF}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[11px] font-bold border border-slate-800/50 text-slate-400 hover:bg-slate-700/40 transition-all">
          <Download className="w-3 h-3" /> Download PDF
        </button>
      </div>
    </div>
  );
}

// ─── Site Trend Group (Section A) ─────────────────────────────────────────────
function SiteTrendGroup({
  site,
  samples,
  isSAWater,
}: {
  site: ApiSite | null;
  samples: ApiSample[];
  isSAWater: boolean;
}) {
  const [open, setOpen] = useState(true);
  // All samples start selected; clicking toggles inclusion in chart
  const [selectedIds, setSelectedIds] = useState<Set<number>>(() => new Set(samples.map(s => s.id)));
  const toggleSample = (id: number) => setSelectedIds(prev => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });
  const allSelected = selectedIds.size === samples.length;
  const toggleAll   = () => setSelectedIds(allSelected ? new Set() : new Set(samples.map(s => s.id)));

  const permitRef = site?.permitRef || site?.permit_ref;
  const standard  = site?.standard || (isSAWater ? 'SA_WATER' : 'AU_EPA');

  const TREND_PARAMS = [
    { key: 'bod5', label: 'BOD₅', color: '#f97316', gradId: 'bodGrad' },
    { key: 'tss',  label: 'SS',   color: '#eab308', gradId: 'tssGrad' },
    { key: 'tds',  label: 'TDS',  color: '#3b82f6', gradId: 'tdsGrad' },
    { key: 'tn',   label: 'TKN',  color: '#8b5cf6', gradId: 'tknGrad' },
    { key: 'totalPhosphorus', label: 'TP', color: '#ec4899', gradId: 'tpGrad' },
  ];

  const sorted = useMemo(() =>
    [...samples].sort((a, b) => {
      const da = parseDate(a.sample_date || a.createdAt || a.created_at)?.getTime() ?? 0;
      const db = parseDate(b.sample_date || b.createdAt || b.created_at)?.getTime() ?? 0;
      return da - db;
    }), [samples]);

  // Only selected samples feed the chart
  const sortedForChart = useMemo(() => sorted.filter(s => selectedIds.has(s.id)), [sorted, selectedIds]);

  const chartData = useMemo(() =>
    sortedForChart.map(s => {
      const p = parseParams(s.params);
      const dateStr = s.sample_date || s.createdAt || s.created_at || '';
      const row: Record<string, unknown> = { name: fmtDateShort(dateStr), fullName: fmtDate(dateStr), sampleName: s.sampleName || s.sample_name || `#${s.id}`, isProjected: false };
      for (const tp of TREND_PARAMS) {
        const v = extractValue(p, tp.key);
        if (v !== null) row[tp.key] = v;
      }
      return row;
    }), [sortedForChart]);

  const activeParams = useMemo(() =>
    TREND_PARAMS.filter(tp =>
      sortedForChart.some(s => {
        const v = extractValue(parseParams(s.params), tp.key);
        return v !== null && v > 0;
      })
    ), [sortedForChart]);

  // ── Future trend projection ──────────────────────────────────────────────────
  const projectedData = useMemo(() => {
    if (sortedForChart.length < 3 || activeParams.length === 0) return [];
    const timestamps = sortedForChart.map(s => {
      const d = parseDate(s.sample_date || s.createdAt || s.created_at);
      return d ? d.getTime() : 0;
    }).filter(t => t > 0);
    if (timestamps.length < 2) return [];
    const lastTs   = timestamps[timestamps.length - 1];
    const firstTs  = timestamps[0];
    const interval = timestamps.length > 1 ? (lastTs - firstTs) / (timestamps.length - 1) : 30 * 24 * 3600000;
    return [1, 2, 3].map(i => {
      const ts   = lastTs + interval * i;
      const date = new Date(ts);
      const row: Record<string, unknown> = {
        name: fmtDateShort(date.toISOString()),
        fullName: fmtDate(date.toISOString()),
        sampleName: 'Projected',
        isProjected: true,
      };
      for (const tp of activeParams) {
        const pairs = timestamps.map((t, idx) => {
          const v = extractValue(parseParams(sortedForChart[idx].params), tp.key);
          return v !== null ? [t, v] as [number, number] : null;
        }).filter(Boolean) as [number, number][];
        if (pairs.length >= 2) {
          const { slope, intercept } = linearRegression(pairs.map(p => p[0]), pairs.map(p => p[1]));
          row[`proj_${tp.key}`] = Math.max(0, slope * ts + intercept);
          if (i === 1) row[`proj_${tp.key}_bridge`] = Math.max(0, slope * lastTs + intercept);
        }
      }
      return row;
    });
  }, [sortedForChart, activeParams]);

  // Bridge: last real point also carries proj_ values so lines connect
  const allChartData = useMemo(() => {
    if (projectedData.length === 0) return chartData;
    const lastReal = { ...chartData[chartData.length - 1] };
    for (const tp of activeParams) {
      const val = lastReal[tp.key];
      if (typeof val === 'number') lastReal[`proj_${tp.key}`] = val;
    }
    return [...chartData.slice(0, -1), lastReal, ...projectedData];
  }, [chartData, projectedData, activeParams]);

  const allParamKeys = useMemo(() => {
    const keys = new Set<string>();
    sorted.forEach(s => Object.keys(parseParams(s.params)).forEach(k => keys.add(k)));
    return Array.from(keys).slice(0, 12);
  }, [sorted]);

  // Latest sample compliance + trend direction
  const latestSample  = sorted[sorted.length - 1];
  const prevSample    = sorted[sorted.length - 2];
  const latestParams  = latestSample ? parseParams(latestSample.params) : {};

  // Summary compliance stats
  const complianceStats = useMemo(() => {
    if (!isSAWater || !latestSample) return null;
    const p = parseParams(latestSample.params);
    let ok = 0, warn = 0, fail = 0;
    for (const [k, limit] of Object.entries(SA_WATER_PERMIT)) {
      if (k.includes('_')) continue;
      const val = extractValue(p, k);
      if (val === null) continue;
      if (val > limit) fail++;
      else if (val > limit * 0.8) warn++;
      else ok++;
    }
    return { ok, warn, fail };
  }, [isSAWater, latestSample]);

  const accentColor = isSAWater ? '#f97316' : 'var(--rb-brand)';
  const accentBg    = isSAWater ? 'bg-orange-500/10' : 'bg-rb-brand/10';
  const accentBorder = isSAWater ? 'border-orange-500/25' : 'border-rb-brand/20';
  const accentText  = isSAWater ? 'text-orange-400' : 'text-rb-brand';

  return (
    <div className={`rounded-xl border overflow-hidden transition-all ${accentBorder} bg-rb-canvas`}
      style={{ boxShadow: open ? `0 0 0 1px ${accentColor}18, 0 4px 24px ${accentColor}08` : 'none' }}>

      {/* Header */}
      <button
        className="w-full flex items-center gap-3 px-4 py-3.5 hover:bg-rb-surface/[0.02] transition-colors text-left group"
        onClick={() => setOpen(o => !o)}
      >
        <div className={`w-9 h-9 rounded-xl flex items-center justify-center shrink-0 border ${accentBg} ${accentBorder}`}
          style={{ boxShadow: `0 0 12px ${accentColor}20` }}>
          <MapPin className={`w-4 h-4 ${accentText}`} />
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap mb-0.5">
            <span className="text-sm font-black text-white">{site ? site.name : 'Unlinked Samples'}</span>
            {isSAWater && (
              <span className="text-[11px] px-2 py-0.5 rounded-full bg-orange-500/15 text-orange-400 border border-orange-500/25 font-bold tracking-tight">🇦🇺 SA Water</span>
            )}
            {site?.standard && !isSAWater && (
              <span className={`text-[11px] px-1.5 py-0.5 rounded-full font-bold border ${stdBadgeClass(site.standard)}`}>{site.standard}</span>
            )}
            <span className={`text-[11px] px-2 py-0.5 rounded-full font-bold border ${accentBg} ${accentText} ${accentBorder}`}>
              {samples.length} sample{samples.length !== 1 ? 's' : ''}
            </span>
            {complianceStats && (
              <span className={`text-[11px] px-2 py-0.5 rounded-full font-bold border ${complianceStats.fail > 0 ? 'bg-red-500/10 text-red-400 border-red-500/20' : complianceStats.warn > 0 ? 'bg-amber-500/10 text-amber-400 border-amber-500/20' : 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20'}`}>
                {complianceStats.fail > 0 ? `⚠ ${complianceStats.fail} over limit` : complianceStats.warn > 0 ? `${complianceStats.warn} near limit` : '✓ All compliant'}
              </span>
            )}
          </div>
          <div className="flex items-center gap-3 text-[11px] text-slate-500">
            {site?.address && <span className="flex items-center gap-1"><MapPin className="w-2.5 h-2.5" />{site.address}</span>}
            {permitRef && <span className="font-mono text-slate-500">Permit: {permitRef}</span>}
          </div>
        </div>

        {/* Open/Close button */}
        <div className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg border transition-all text-[11px] font-bold
          ${open
            ? `${accentBg} ${accentText} ${accentBorder}`
            : 'bg-slate-800/60 text-slate-400 border-slate-800/50 group-hover:border-slate-600 group-hover:text-slate-300'}`}>
          {open ? (
            <><ChevronDown className="w-3.5 h-3.5" />Collapse</>
          ) : (
            <><ChevronRight className="w-3.5 h-3.5" />Open</>
          )}
        </div>
      </button>

      <AnimatePresence>
        {open && (
          <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.22 }} className="overflow-hidden">
            <div className="border-t border-white/[0.04] px-4 pt-4 pb-5 space-y-4" style={{ background: 'linear-gradient(180deg, rgba(255,255,255,0.02) 0%, transparent 60%)' }}>

              {/* ── Sample table ── */}
              <div className="overflow-x-auto rounded-xl border border-slate-800/40"
                style={{ boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.04)' }}>
                <table className="w-full text-[11px]">
                  <thead>
                    <tr style={{ background: 'linear-gradient(90deg, rgba(15,23,42,0.9) 0%, rgba(30,41,59,0.6) 100%)' }}
                      className="border-b border-slate-800/50">
                      {/* Toggle-all button */}
                      <th className="px-3 py-2.5 w-10">
                        <button
                          onClick={toggleAll}
                          title={allSelected ? 'Deselect all' : 'Select all'}
                          className={`w-5 h-5 rounded-md border flex items-center justify-center transition-all text-[9px] font-black
                            ${allSelected
                              ? `${accentBg} ${accentBorder} ${accentText}`
                              : 'bg-slate-800/60 border-slate-700/50 text-slate-500 hover:border-slate-500'}`}
                          style={allSelected ? { boxShadow: `0 0 6px ${accentColor}40` } : undefined}
                        >
                          {allSelected ? '✓' : selectedIds.size > 0 ? '–' : '○'}
                        </button>
                      </th>
                      <th className="text-left px-3 py-2.5 text-slate-400 font-bold min-w-[130px]">Sample</th>
                      <th className="text-left px-3 py-2.5 text-slate-400 font-bold whitespace-nowrap">Date</th>
                      {['bod5','tss','tds','tn','totalPhosphorus','ph'].map(k => (
                        <th key={k} className="text-right px-2 py-2.5 font-bold whitespace-nowrap"
                          style={{ color: TREND_PARAMS.find(p => p.key === k)?.color || 'var(--rb-text-3)' }}>
                          {PARAM_LABELS[k] || k}
                        </th>
                      ))}
                      {allParamKeys.filter(k => !['bod5','tss','tds','tn','totalPhosphorus','ph','bod','BOD5','BOD','TSS','TDS','TN','tkn','TKN'].includes(k)).slice(0,4).map(k => (
                        <th key={k} className="text-right px-2 py-2.5 text-slate-400 font-bold whitespace-nowrap">
                          {PARAM_LABELS[k] || k.toUpperCase()}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {sorted.map((s, i) => {
                      const p        = parseParams(s.params);
                      const dateStr  = s.sample_date || s.createdAt || s.created_at;
                      const name     = s.sampleName || s.sample_name || s.reportNumber || s.report_number || `Sample #${s.id}`;
                      const extraKeys = allParamKeys.filter(k => !['bod5','tss','tds','tn','totalPhosphorus','ph','bod','BOD5','BOD','TSS','TDS','TN','tkn','TKN'].includes(k)).slice(0,4);
                      const stdCode  = isSAWater ? 'SA_WATER' : standard;
                      const isLatest = i === sorted.length - 1;
                      const isOn     = selectedIds.has(s.id);
                      return (
                        <tr key={s.id}
                          onClick={() => toggleSample(s.id)}
                          className={`rb-sample-row border-b border-slate-800/30 last:border-0 ${!isOn ? 'rb-sample-off' : ''}`}
                          style={{
                            background: isOn
                              ? (isLatest
                                  ? (isSAWater ? 'rgba(249,115,22,0.07)' : 'rgba(var(--rb-brand-rgb),0.06)')
                                  : i % 2 === 0 ? 'transparent' : 'rgba(255,255,255,0.012)')
                              : 'rgba(15,23,42,0.4)',
                            boxShadow: isOn && isLatest ? `inset 3px 0 0 ${accentColor}50` : isOn ? undefined : 'inset 3px 0 0 rgba(100,116,139,0.15)',
                          }}
                        >
                          {/* Toggle indicator cell */}
                          <td className="px-3 py-2.5 w-10" onClick={e => e.stopPropagation()}>
                            <button
                              onClick={() => toggleSample(s.id)}
                              className={`w-5 h-5 rounded-md border flex items-center justify-center transition-all text-[9px] font-black
                                ${isOn
                                  ? `${accentBg} ${accentBorder} ${accentText}`
                                  : 'bg-slate-800/80 border-slate-700/60 text-slate-600'}`}
                              style={isOn ? { boxShadow: `0 0 6px ${accentColor}35, inset 0 1px 0 rgba(255,255,255,0.1)` } : {
                                boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.05), inset 0 -1px 0 rgba(0,0,0,0.2)'
                              }}
                            >
                              {isOn ? '✓' : '○'}
                            </button>
                          </td>
                          <td className="px-3 py-2.5 max-w-[160px]">
                            <div className="flex items-center gap-1.5">
                              <span className={`text-[10px] font-mono font-bold ${isOn && isLatest ? accentText : isOn ? 'text-slate-500' : 'text-slate-400'}`}>
                                S-{String(i+1).padStart(3,'0')}
                              </span>
                              {isLatest && isOn && <span className="text-[9px] text-slate-600">latest</span>}
                            </div>
                            <div className={`text-[11px] font-semibold truncate ${isOn ? 'text-slate-300' : 'text-slate-600'}`}>{name}</div>
                          </td>
                          <td className={`px-3 py-2.5 whitespace-nowrap ${isOn ? 'text-slate-400' : 'text-slate-400'}`}>{fmtDate(dateStr)}</td>
                          {['bod5','tss','tds','tn','totalPhosphorus','ph'].map(k => {
                            const val = extractValue(p, k);
                            if (val === null) return <td key={k} className="px-2 py-2.5 text-right text-slate-400">—</td>;
                            const colour = isSAWater ? (() => {
                              const limit = SA_WATER_PERMIT[k];
                              if (!limit) return 'neutral';
                              if (val > limit) return 'red';
                              if (val > limit * 0.8) return 'amber';
                              return 'green';
                            })() : paramColour(k, val, stdCode);
                            // Trend arrow vs prev sample
                            const prevP = prevSample ? parseParams(prevSample.params) : null;
                            const prevVal = prevP ? extractValue(prevP, k) : null;
                            const trend = isLatest && prevVal !== null ? (val > prevVal ? '↑' : val < prevVal ? '↓' : '→') : '';
                            const trendCls = trend === '↑' ? 'text-red-400' : trend === '↓' ? 'text-emerald-400' : 'text-slate-500';
                            return (
                              <td key={k} className="px-2 py-2.5 text-right">
                                <div className="inline-flex items-center gap-0.5">
                                  <span className={`inline-block px-1.5 py-0.5 rounded-lg text-[11px] font-bold border ${COLOUR_CLASSES[colour]}`}
                                    style={{ boxShadow: colour === 'red' ? '0 0 6px rgba(239,68,68,0.3)' : colour === 'green' ? '0 0 6px rgba(16,185,129,0.15)' : 'none' }}>
                                    {val.toFixed(k === 'ph' ? 2 : 1)}
                                  </span>
                                  {trend && <span className={`text-[10px] font-bold ${trendCls}`}>{trend}</span>}
                                </div>
                              </td>
                            );
                          })}
                          {extraKeys.map(k => {
                            const val = p[k];
                            if (typeof val !== 'number') return <td key={k} className="px-2 py-2.5 text-right text-slate-400">—</td>;
                            return <td key={k} className="px-2 py-2.5 text-right text-slate-400 font-mono">{val.toFixed(1)}</td>;
                          })}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* ── Trend chart with projections ── */}
              {chartData.length >= 2 && activeParams.length > 0 && (
                <div className="rounded-xl border border-slate-800/40 overflow-hidden"
                  style={{ background: 'linear-gradient(135deg, #080e1c 0%, #0d1527 100%)', boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.04)' }}>
                  <div className="px-4 pt-3.5 pb-1 flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <div className={`w-6 h-6 rounded-lg ${accentBg} border ${accentBorder} flex items-center justify-center`}>
                        <TrendingUp className={`w-3 h-3 ${accentText}`} />
                      </div>
                      <span className="text-[11px] font-bold text-slate-300">Parameter Trend</span>
                      {projectedData.length > 0 && (
                        <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-violet-500/10 text-violet-400 border border-violet-500/20 font-bold">
                          +{projectedData.length} projected
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      {activeParams.map(tp => (
                        <div key={tp.key} className="flex items-center gap-1">
                          <div className="w-2 h-2 rounded-full" style={{ backgroundColor: tp.color }} />
                          <span className="text-[10px] text-slate-500">{tp.label}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                  <ResponsiveContainer width="100%" height={260}>
                    <AreaChart data={allChartData} margin={{ top: 8, right: 16, bottom: 4, left: 0 }}>
                      <defs>
                        {activeParams.map(tp => (
                          <linearGradient key={tp.gradId} id={tp.gradId} x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%"  stopColor={tp.color} stopOpacity={0.25} />
                            <stop offset="95%" stopColor={tp.color} stopOpacity={0.02} />
                          </linearGradient>
                        ))}
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke="rgba(100,116,139,0.08)" vertical={false} />
                      <XAxis
                        dataKey="name"
                        tick={{ fontSize: 10, fill: 'var(--rb-text-4)' }}
                        axisLine={false} tickLine={false}
                      />
                      <YAxis
                        tick={{ fontSize: 10, fill: 'var(--rb-text-4)' }}
                        axisLine={false} tickLine={false}
                        width={42}
                      />
                      <ChartTooltip
                        content={({ active, payload, label }) => {
                          if (!active || !payload?.length) return null;
                          const isProj = payload[0]?.payload?.isProjected;
                          return (
                            <div className="rounded-xl border border-slate-800/60 bg-rb-elevated/95 backdrop-blur-sm p-3 shadow-2xl min-w-[140px]">
                              <p className="text-[10px] text-slate-400 mb-2 flex items-center gap-1.5">
                                {isProj && <span className="text-[9px] px-1 py-0.5 rounded bg-violet-500/20 text-violet-400 font-bold">PROJECTED</span>}
                                {payload[0]?.payload?.fullName || label}
                              </p>
                              {payload.map((p: any) => p.value != null && (
                                <div key={p.dataKey} className="flex items-center justify-between gap-4 text-[11px]">
                                  <div className="flex items-center gap-1.5">
                                    <div className="w-2 h-2 rounded-full" style={{ backgroundColor: p.color }} />
                                    <span className="text-slate-400">{p.name}</span>
                                  </div>
                                  <span className="font-bold text-white">{Number(p.value).toFixed(1)}</span>
                                </div>
                              ))}
                            </div>
                          );
                        }}
                      />
                      {isSAWater && Object.entries(SA_WATER_PERMIT).filter(([k]) => k !== 'ph_high' && k !== 'ph_low').map(([k, v]) => {
                        const param = activeParams.find(p => p.key === k);
                        if (!param) return null;
                        return (
                          <ReferenceLine key={k} y={v} stroke="#ef4444" strokeDasharray="5 3" strokeWidth={1}
                            label={{ value: `${param.label} limit`, fill: '#ef4444', fontSize: 9, position: 'insideTopRight' }} />
                        );
                      })}
                      {/* Projection region marker */}
                      {projectedData.length > 0 && (
                        <ReferenceLine
                          x={chartData[chartData.length - 1]?.name as string}
                          stroke="rgba(139,92,246,0.3)" strokeDasharray="3 3" strokeWidth={1}
                          label={{ value: 'now', fill: '#7c3aed', fontSize: 9 }}
                        />
                      )}
                      {/* Actual area series */}
                      {activeParams.map(tp => (
                        <Area
                          key={tp.key} type="monotone" dataKey={tp.key}
                          name={tp.label} stroke={tp.color} strokeWidth={2.5}
                          fill={`url(#${tp.gradId})`} fillOpacity={1}
                          dot={false}
                          activeDot={{ r: 5, strokeWidth: 2, stroke: tp.color, fill: 'var(--rb-elevated)' }}
                          connectNulls
                        />
                      ))}
                      {/* Projected dashed series */}
                      {activeParams.map(tp => (
                        <Line
                          key={`proj_${tp.key}`} type="monotone" dataKey={`proj_${tp.key}`}
                          name={`${tp.label} trend`} stroke={tp.color} strokeWidth={1.5}
                          strokeDasharray="5 4" strokeOpacity={0.55}
                          dot={{ r: 3, fill: 'transparent', stroke: tp.color, strokeOpacity: 0.6 }}
                          activeDot={{ r: 4 }} connectNulls legendType="none"
                        />
                      ))}
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              )}

              {/* ── SA Water compliance strip ── */}
              {isSAWater && latestSample && (
                <div className="rounded-xl border border-orange-500/15 bg-orange-500/5 px-4 py-3">
                  <p className="text-[10px] text-orange-400/70 font-bold uppercase tracking-wider mb-2.5 flex items-center gap-1.5">
                    <CheckCircle2 className="w-3 h-3" /> Latest sample — SA Water permit compliance
                  </p>
                  <div className="flex flex-wrap gap-2">
                  {Object.entries(SA_WATER_PERMIT).filter(([k]) => !k.includes('_')).map(([k, limit]) => {
                    const val = extractValue(latestParams, k);
                    if (val === null) return null;
                    const over = val > limit;
                    const warn = !over && val > limit * 0.8;
                    return (
                      <span key={k} className={`text-[11px] px-2 py-0.5 rounded-lg border font-bold ${over ? 'bg-red-500/15 text-red-400 border-red-500/25' : warn ? 'bg-amber-500/10 text-amber-400 border-amber-500/20' : 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20'}`}
                        style={over ? { boxShadow: '0 0 8px rgba(239,68,68,0.2)' } : undefined}
                      >
                        {PARAM_LABELS[k] || k} {val.toFixed(1)} {over ? '⚠' : '✓'}
                      </span>
                    );
                  })}
                  </div>
                </div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

// ─── SA Water Site Section B Group ───────────────────────────────────────────
function SAWaterSiteSection({
  site, samples, estimates, customerName, preparedBy, onEstimateSaved,
}: {
  site: ApiSite;
  samples: ApiSample[];
  estimates: any[];
  customerName: string | null;
  preparedBy?: string;
  onEstimateSaved: () => void;
}) {
  const [open, setOpen]                     = useState(true);
  const [expandedSampleId, setExpandedSampleId] = useState<number | null>(null);

  const permitRef    = site.permitRef || site.permit_ref;
  const latestEst    = estimates.length ? estimates.reduce((a, b) => (parseDate(b.created_at)?.getTime() ?? 0) > (parseDate(a.created_at)?.getTime() ?? 0) ? b : a) : null;
  const latestCharge = latestEst ? Number(latestEst.total_charge) : 0;
  const hasCharges   = latestCharge > 0;

  const sortedSamples = useMemo(() =>
    [...samples].sort((a, b) => {
      const da = parseDate(a.sample_date || a.createdAt || a.created_at)?.getTime() ?? 0;
      const db = parseDate(b.sample_date || b.createdAt || b.created_at)?.getTime() ?? 0;
      return db - da;
    }), [samples]);

  const sortedEstimates = useMemo(() =>
    [...estimates].sort((a, b) => (parseDate(b.created_at)?.getTime() ?? 0) - (parseDate(a.created_at)?.getTime() ?? 0)),
    [estimates]);

  return (
    <motion.div initial={{ opacity: 0, y: 4 }} animate={{ opacity: 1, y: 0 }}
      className="rounded-xl border border-orange-500/25 bg-rb-canvas overflow-hidden"
      style={{ boxShadow: open ? '0 0 0 1px rgba(249,115,22,0.12), 0 4px 24px rgba(249,115,22,0.06)' : 'none' }}>
      <button className="w-full flex items-center gap-3 px-4 py-3.5 hover:bg-rb-surface/[0.02] transition-colors text-left group" onClick={() => setOpen(o => !o)}>
        <div className="w-10 h-10 rounded-xl bg-orange-500/10 border border-orange-500/25 flex items-center justify-center shrink-0"
          style={{ boxShadow: '0 0 14px rgba(249,115,22,0.15)' }}>
          <MapPin className="w-4 h-4 text-orange-400" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap mb-0.5">
            <span className="text-sm font-black text-white">{site.name}</span>
            <span className="text-[11px] px-2 py-0.5 rounded-full bg-orange-500/15 text-orange-400 border border-orange-500/25 font-bold">🇦🇺 SA Water</span>
            {permitRef && (
              <span className="text-[11px] px-1.5 py-0.5 rounded-full bg-slate-800/80 text-slate-400 border border-slate-800/50 font-mono">Permit {permitRef}</span>
            )}
          </div>
          <div className="flex items-center gap-3 flex-wrap text-[11px]">
            <span className="text-slate-500">{samples.length} sample{samples.length !== 1 ? 's' : ''}</span>
            <span className="text-slate-400">·</span>
            <span className="text-slate-500">{estimates.length} estimate{estimates.length !== 1 ? 's' : ''}</span>
            {latestEst && (
              <span className={`font-bold ${hasCharges ? 'text-red-400' : 'text-emerald-400'}`}>
                {hasCharges ? <><DollarSign className="w-3 h-3 inline" />{fmtAUD(latestCharge)}/yr est.</> : '✓ No charges'}
              </span>
            )}
          </div>
        </div>
        {/* Open/Collapse button */}
        <div className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg border transition-all text-[11px] font-bold
          ${open
            ? 'bg-orange-500/10 text-orange-400 border-orange-500/25'
            : 'bg-slate-800/60 text-slate-400 border-slate-800/50 group-hover:border-slate-600 group-hover:text-slate-300'}`}>
          {open ? (
            <><ChevronDown className="w-3.5 h-3.5" />Collapse</>
          ) : (
            <><ChevronRight className="w-3.5 h-3.5" />Open</>
          )}
        </div>
      </button>

      <AnimatePresence>
        {open && (
          <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.2 }} className="overflow-hidden">
            <div className="border-t border-orange-500/15 px-4 pt-4 pb-5 space-y-5">

              {/* (i) Effluent samples */}
              <div>
                <p className="text-[10px] text-slate-500 uppercase tracking-wider font-bold mb-2 flex items-center gap-1">
                  <Beaker className="w-3 h-3" /> Effluent Samples at this site
                </p>
                {sortedSamples.length === 0 ? (
                  <div className="flex items-center gap-2.5 px-3.5 py-3 rounded-lg border border-amber-500/25 bg-amber-500/5">
                    <AlertTriangle className="w-4 h-4 text-amber-500 shrink-0" />
                    <p className="text-[11px] font-bold text-amber-400">No effluent samples linked to this site</p>
                  </div>
                ) : (
                  <div className="space-y-1.5">
                    {sortedSamples.map(s => {
                      const p = parseParams(s.params);
                      const name = s.sampleName || s.sample_name || s.reportNumber || s.report_number || `Sample #${s.id}`;
                      const dateStr = s.sample_date || s.createdAt || s.created_at;
                      const bod = extractValue(p, 'bod5');
                      const tss = extractValue(p, 'tss');
                      const tds = extractValue(p, 'tds');
                      const isExpanded = expandedSampleId === s.id;
                      return (
                        <div key={s.id} className="border border-slate-800/40 rounded-xl overflow-hidden">
                          <div
                            className="flex items-center gap-3 px-3 py-2.5 hover:bg-orange-500/5 cursor-pointer transition-colors"
                            onClick={() => setExpandedSampleId(isExpanded ? null : s.id)}
                          >
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 flex-wrap">
                                <span className="text-[11px] font-bold text-slate-300 font-mono truncate">{name}</span>
                                <span className="text-[10px] text-slate-500 whitespace-nowrap">{fmtDate(dateStr)}</span>
                              </div>
                              <div className="flex items-center gap-2 mt-0.5 text-[10px] font-mono">
                                {bod !== null && <span className="text-slate-500">BOD <span className="font-bold" style={{ color: '#f97316' }}>{bod.toFixed(0)}</span></span>}
                                {tss !== null && <span className="text-slate-500">SS <span className="font-bold" style={{ color: '#eab308' }}>{tss.toFixed(0)}</span></span>}
                                {tds !== null && <span className="text-slate-500">TDS <span className="font-bold" style={{ color: '#3b82f6' }}>{tds.toFixed(0)}</span></span>}
                              </div>
                            </div>
                            <button
                              onClick={e => { e.stopPropagation(); setExpandedSampleId(isExpanded ? null : s.id); }}
                              className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-[10px] font-bold bg-orange-500/10 text-orange-400 border border-orange-500/20 hover:bg-orange-500/20 transition-all shrink-0"
                            >
                              <Gauge className="w-3 h-3" /> Estimate Charges
                            </button>
                            <ChevronDown className={`w-3.5 h-3.5 text-slate-500 shrink-0 transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
                          </div>
                          <AnimatePresence>
                            {isExpanded && (
                              <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.18 }} className="overflow-hidden">
                                <div className="border-t border-slate-800/30 px-3 pb-3">
                                  <InlineSAWaterCalc
                                    sample={s}
                                    site={site}
                                    customerName={customerName}
                                    preparedBy={preparedBy}
                                    onSaved={() => { onEstimateSaved(); setExpandedSampleId(null); }}
                                  />
                                </div>
                              </motion.div>
                            )}
                          </AnimatePresence>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* (ii) Saved estimates */}
              <div>
                <p className="text-[10px] text-slate-500 uppercase tracking-wider font-bold mb-2 flex items-center gap-2">
                  <Receipt className="w-3 h-3" /> Saved Estimates
                  <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-orange-500/10 text-orange-400 border border-orange-500/20 font-bold">{estimates.length}</span>
                </p>
                {sortedEstimates.length === 0 ? (
                  <p className="text-[10px] text-slate-600 italic px-1">No saved estimates yet — use "Estimate Charges" on a sample above.</p>
                ) : (
                  <div className="space-y-2">
                    {sortedEstimates.map((est, i) => (
                      <SAWaterEstimateRow key={est.id || i} est={est} customerName={customerName} />
                    ))}
                  </div>
                )}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────
export default function EffluentReportsPage() {
  const navigate    = useNavigate();
  const { user }    = useAuth();
  const { currentCustomer } = useSession();

  const customerId   = currentCustomer?.serverId ?? (currentCustomer ? parseInt(currentCustomer.id) : null);
  const customerName = currentCustomer ? (currentCustomer.company || currentCustomer.name) : null;
  const preparedBy   = user?.firstName && user?.lastName
    ? `${user.firstName} ${user.lastName}`
    : (user as any)?.name || (user as any)?.username || undefined;

  // ── Data state ──────────────────────────────────────────────────────────────
  const [allSites,         setAllSites]         = useState<ApiSite[]>([]);
  const [effluentSamples,  setEffluentSamples]  = useState<ApiSample[]>([]);
  const [trendReports,     setTrendReports]      = useState<any[]>([]);
  const [saWaterEstimates, setSaWaterEstimates] = useState<any[]>([]);
  const [serverReports,    setServerReports]    = useState<any[]>([]);

  const [loadingSites,   setLoadingSites]   = useState(false);
  const [loadingSamples, setLoadingSamples] = useState(false);
  const [loadingReports, setLoadingReports] = useState(false);
  const [loadingEst,     setLoadingEst]     = useState(false);

  // ── Section expand state ────────────────────────────────────────────────────
  const [secAOpen, setSecAOpen] = useState(true);
  const [secBOpen, setSecBOpen] = useState(true);

  // ── Report list state ───────────────────────────────────────────────────────
  const [search,        setSearch]        = useState('');
  const [expandedId,    setExpandedId]    = useState<string | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);
  const [syncingId,     setSyncingId]     = useState<string | null>(null);
  const [previewUrl,    setPreviewUrl]    = useState<string | null>(null);
  const [previewTitle,  setPreviewTitle]  = useState('');
  const [previewDownloader, setPreviewDownloader] = useState<() => void>(() => () => {});

  // ── Load sites ──────────────────────────────────────────────────────────────
  const loadSites = useCallback(async () => {
    if (!customerId || !hasAuthToken()) { setAllSites([]); return; }
    setLoadingSites(true);
    try { setAllSites(await apiListCustomerSites(customerId) || []); }
    catch { setAllSites([]); }
    finally { setLoadingSites(false); }
  }, [customerId]);

  // ── Load effluent samples ───────────────────────────────────────────────────
  const loadSamples = useCallback(async () => {
    if (!customerId || !hasAuthToken()) { setEffluentSamples([]); return; }
    setLoadingSamples(true);
    try {
      const [influentRows, customerRows] = await Promise.all([
        apiListInfluentSamples(customerId, { type: 'effluent' }).catch(() => [] as any[]),
        apiListCustomerSamples(customerId).catch(() => [] as any[]),
      ]);
      const seen = new Set<number>();
      const merged: ApiSample[] = [];
      for (const s of [...(influentRows || []), ...(customerRows || [])]) {
        const id = Number(s.id);
        if (seen.has(id)) continue;
        const typ = (s.sampleType ?? s.sample_type ?? '').toLowerCase();
        if (typ === 'influent') continue;
        seen.add(id);
        merged.push(s);
      }
      setEffluentSamples(merged);
    } catch { setEffluentSamples([]); }
    finally { setLoadingSamples(false); }
  }, [customerId]);

  // ── Load reports ────────────────────────────────────────────────────────────
  const loadReports = useCallback(async () => {
    if (!hasPhpBackend()) return;
    setLoadingReports(true);
    try { setServerReports(await apiListReports() || []); }
    catch { setServerReports([]); }
    finally { setLoadingReports(false); }
  }, []);

  // ── Load SA Water estimates ─────────────────────────────────────────────────
  const loadEstimates = useCallback(async () => {
    if (!customerId || !hasAuthToken()) { setSaWaterEstimates([]); return; }
    setLoadingEst(true);
    try { setSaWaterEstimates(await apiListSAWaterEstimates(customerId) || []); }
    catch { setSaWaterEstimates([]); }
    finally { setLoadingEst(false); }
  }, [customerId]);

  useEffect(() => { loadSites(); },     [loadSites]);
  useEffect(() => { loadSamples(); },   [loadSamples]);
  useEffect(() => { loadReports(); },   [loadReports]);
  useEffect(() => { loadEstimates(); }, [loadEstimates]);

  // ── Computed data ───────────────────────────────────────────────────────────
  const saSites      = useMemo(() => allSites.filter(s => s.standard === 'SA_WATER' || s.standard === 'sa_water'), [allSites]);
  const regularSites = useMemo(() => allSites.filter(s => s.standard !== 'SA_WATER' && s.standard !== 'sa_water'), [allSites]);
  const saSiteIds    = useMemo(() => new Set(saSites.map(s => s.id)), [saSites]);

  const samplesBySite = useMemo<Map<string, ApiSample[]>>(() => {
    const m = new Map<string, ApiSample[]>();
    for (const s of effluentSamples) {
      const sid = String(s.siteId ?? s.site_id ?? 'unlinked');
      const arr = m.get(sid) ?? [];
      arr.push(s);
      m.set(sid, arr);
    }
    return m;
  }, [effluentSamples]);

  const estimatesBySite = useMemo<Map<number, any[]>>(() => {
    const m = new Map<number, any[]>();
    for (const e of saWaterEstimates) {
      const sid = Number(e.site_id);
      const arr = m.get(sid) ?? [];
      arr.push(e);
      m.set(sid, arr);
    }
    return m;
  }, [saWaterEstimates]);

  // Local reports from localStorage
  const localReports = useMemo(() => {
    try {
      const all: any[] = JSON.parse(localStorage.getItem('rb_sample_reports') || '[]');
      const uid = user ? Number((user as any).id) : 0;
      return uid ? all.filter(r => !r.userId || Number(r.userId) === uid) : all;
    } catch { return []; }
  }, [user, serverReports]);

  const norm = (raw: any[]) => raw.map((s: any) => ({
    name: s.name || s.sampleName || 'Unknown',
    date: s.date || new Date().toISOString().split('T')[0],
    params: typeof s.params === 'string' ? JSON.parse(s.params) : (s.params || {}),
  }));

  // Merged effluent reports (server + local)
  const allReports = useMemo(() => {
    const fromServer = serverReports
      .filter(r => (r.reportType ?? r.report_type ?? '') === 'effluent')
      .map((r: any) => {
        const rn  = r.reportNumber ?? r.report_number;
        const std = r.standard || 'UK_EA';
        const title = (r.reportTitle ?? r.report_title ?? '');
        return {
          id: `srv_${r.id}`,
          reportNumber: normalizeReportNum(rn, std),
          reportTitle: (title && title !== 'Untitled')
            ? title
            : [r.customerName ?? r.customer_name, r.siteName ?? r.site_name].filter(Boolean).join(' — ') || 'Effluent Quality Report',
          customerName: r.customerName ?? r.customer_name ?? undefined,
          siteName: r.siteName ?? r.site_name ?? undefined,
          siteAddress: r.siteAddress ?? r.site_address ?? undefined,
          sitePermitRef: r.sitePermitRef ?? r.site_permit_ref ?? undefined,
          siteJurisdiction: r.siteJurisdiction ?? r.site_jurisdiction ?? undefined,
          standard: std,
          createdAt: r.createdAt ?? r.created_at ?? null,
          source: 'server' as const,
          samples: Array.isArray(r.samples) ? norm(r.samples) : [],
        };
      });
    const fromLocal = localReports
      .filter(r => (r.reportType ?? r.report_type ?? '') === 'effluent')
      .map((r: any) => {
        const std = r.standard || 'UK_EA';
        return {
          id: `loc_${r.id}`,
          reportNumber: normalizeReportNum(r.reportNumber, std),
          reportTitle: (r.reportTitle && r.reportTitle !== 'Untitled') ? r.reportTitle : 'Effluent Quality Report',
          customerName: r.customerName ?? undefined,
          siteName: r.siteName ?? undefined,
          siteAddress: r.siteAddress ?? undefined,
          sitePermitRef: r.sitePermitRef ?? undefined,
          siteJurisdiction: r.siteJurisdiction ?? undefined,
          standard: std,
          createdAt: r.createdAt ?? null,
          source: 'local' as const,
          samples: Array.isArray(r.samples) ? norm(r.samples) : [],
        };
      });
    const serverNums = new Set(fromServer.map(r => r.reportNumber));
    return [...fromServer, ...fromLocal.filter(r => !serverNums.has(r.reportNumber))];
  }, [serverReports, localReports]);

  type R = typeof allReports[0];

  // Filtered to current customer
  const customerFiltered = useMemo(() => {
    if (!customerName) return allReports;
    const qName = customerName.toLowerCase();
    return allReports.filter(r => !r.customerName || r.customerName.toLowerCase() === qName);
  }, [allReports, customerName]);

  // Set trendReports for stat tile
  useEffect(() => {
    setTrendReports(customerFiltered);
  }, [customerFiltered]);

  const filteredReports = useMemo(() => {
    let list = [...customerFiltered];
    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter(r =>
        r.reportNumber.toLowerCase().includes(q) ||
        r.reportTitle.toLowerCase().includes(q) ||
        (r.customerName || '').toLowerCase().includes(q) ||
        (r.siteName || '').toLowerCase().includes(q)
      );
    }
    return list.sort((a, b) => {
      const ta = parseDate(a.createdAt)?.getTime() ?? 0;
      const tb = parseDate(b.createdAt)?.getTime() ?? 0;
      return tb - ta;
    });
  }, [customerFiltered, search]);

  // ── Site groups for Section A ───────────────────────────────────────────────
  const sectionASiteGroups = useMemo(() => {
    const groups: { site: ApiSite | null; samples: ApiSample[]; isSAWater: boolean; key: string }[] = [];
    // All sites that have samples
    for (const site of allSites) {
      const samples = samplesBySite.get(String(site.id)) || [];
      if (samples.length > 0) {
        groups.push({ site, samples, isSAWater: saSiteIds.has(site.id), key: `site_${site.id}` });
      }
    }
    // Unlinked
    const unlinked = samplesBySite.get('unlinked') || [];
    if (unlinked.length > 0) {
      groups.push({ site: null, samples: unlinked, isSAWater: false, key: 'unlinked' });
    }
    return groups;
  }, [allSites, samplesBySite, saSiteIds]);

  // ── Report action helpers ───────────────────────────────────────────────────
  const buildOpts = useCallback((r: R) => ({
    samples: r.samples.map(s => ({ name: s.name, date: new Date(s.date), params: s.params })),
    opts: {
      reportNumber: r.reportNumber,
      reportTitle: r.reportTitle,
      customerName: r.customerName,
      siteName: r.siteName,
      siteAddress: r.siteAddress,
      permitRef: r.sitePermitRef,
      regulatoryAuthority: r.siteJurisdiction,
      standard: r.standard,
      createdAt: parseDate(r.createdAt) ?? new Date(),
    },
  }), []);

  const handlePreview = useCallback((r: R, e?: React.MouseEvent) => {
    e?.stopPropagation();
    try {
      const { samples, opts } = buildOpts(r);
      const url = generateSampleTrendReportPDF(samples, opts, 'preview');
      if (!url) { toast.error('Preview failed'); return; }
      setPreviewUrl(url);
      setPreviewTitle(`${r.reportNumber} — ${r.reportTitle}`);
      setPreviewDownloader(() => () => { generateSampleTrendReportPDF(samples, opts, 'save'); toast.success(`Downloaded: ${r.reportNumber}`); });
    } catch (err: any) { toast.error(`Preview failed: ${err.message}`); }
  }, [buildOpts]);

  const handlePDF = useCallback((r: R, e?: React.MouseEvent) => {
    e?.stopPropagation();
    try {
      const { samples, opts } = buildOpts(r);
      generateSampleTrendReportPDF(samples, opts, 'save');
      toast.success(`Downloaded: ${r.reportNumber}`);
    } catch (err: any) { toast.error(`PDF failed: ${err.message}`); }
  }, [buildOpts]);

  const handleDelete = useCallback(async (r: R, e?: React.MouseEvent) => {
    e?.stopPropagation();
    if (r.source === 'server') {
      try { await apiDeleteReport(parseInt(r.id.replace('srv_', ''))); toast.info('Deleted'); loadReports(); }
      catch (err: any) { toast.error(err.message); }
    } else {
      const local = JSON.parse(localStorage.getItem('rb_sample_reports') || '[]').filter((x: any) => `loc_${x.id}` !== r.id);
      localStorage.setItem('rb_sample_reports', JSON.stringify(local));
      toast.info('Removed'); window.location.reload();
    }
    setDeleteConfirm(null);
  }, [loadReports]);

  const handleSync = useCallback(async (r: R, e?: React.MouseEvent) => {
    e?.stopPropagation();
    if (!hasPhpBackend()) { toast.error('No DB connection'); return; }
    setSyncingId(r.id);
    try {
      const localId = r.id.replace('loc_', '');
      const raw: any[] = JSON.parse(localStorage.getItem('rb_sample_reports') || '[]');
      const entry = raw.find((x: any) => String(x.id) === localId) || { ...r, reportType: 'effluent' };
      await apiSaveReport(entry);
      localStorage.setItem('rb_sample_reports', JSON.stringify(raw.filter((x: any) => String(x.id) !== localId)));
      toast.success(`Synced: ${r.reportNumber}`);
      await loadReports();
    } catch (err: any) { toast.error(`Sync failed: ${err?.message}`); }
    finally { setSyncingId(null); }
  }, [loadReports]);

  // ── PDF Preview modal ───────────────────────────────────────────────────────
  if (previewUrl) {
    return (
      <motion.div
        initial={{ opacity: 0 }} animate={{ opacity: 1 }}
        className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-3"
        onClick={e => { if (e.target === e.currentTarget) setPreviewUrl(null); }}
      >
        <motion.div
          initial={{ scale: 0.95 }} animate={{ scale: 1 }}
          className="bg-rb-surface rounded-2xl w-full max-w-5xl flex flex-col shadow-2xl overflow-hidden"
          style={{ height: '92vh' }}
        >
          <div className="flex items-center justify-between px-4 py-3 bg-rb-surface border-b border-rb-brand/20 shrink-0">
            <div className="flex items-center gap-3">
              <Beaker className="w-4 h-4 text-rb-brand" />
              <span className="text-sm font-bold text-white truncate max-w-[400px]">{previewTitle}</span>
            </div>
            <div className="flex items-center gap-2">
              <button onClick={previewDownloader} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold bg-rb-brand text-slate-200 hover:bg-yellow-400">
                <Download className="w-3.5 h-3.5" /> Download PDF
              </button>
              <button onClick={() => setPreviewUrl(null)} className="p-1.5 rounded-lg text-slate-400 hover:text-white hover:bg-slate-800">
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>
          <iframe src={previewUrl} className="flex-1 w-full" title="PDF Preview" style={{ border: 'none' }} />
        </motion.div>
      </motion.div>
    );
  }

  // ── Main render ─────────────────────────────────────────────────────────────
  return (
    <div className="space-y-6">

      <PageHeader
        icon={Beaker}
        iconColor="text-rb-brand"
        title="Effluent Reports"
        subtitle="Discharge quality analysis, compliance monitoring and SA Water trade waste estimates"
        actions={
          <button onClick={() => { loadSites(); loadSamples(); loadReports(); loadEstimates(); }}
            className="p-2 rounded-lg text-slate-500 hover:text-slate-300 hover:bg-slate-800 border border-transparent hover:border-slate-800/60 transition-all" title="Refresh all">
            <RefreshCw className={`w-4 h-4 ${(loadingReports || loadingSamples || loadingSites) ? 'animate-spin' : ''}`} />
          </button>
        }
      />

      {/* Stat tiles */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {[
          { label: 'Effluent Samples', value: effluentSamples.length, icon: Beaker,   color: 'var(--rb-brand)', glow: 'rgba(205,164,46,0.15)' },
          { label: 'SA Water Sites',   value: saSites.length,         icon: Droplets, color: '#f97316', glow: 'rgba(249,115,22,0.15)' },
          { label: 'Trend Reports',    value: trendReports.length,    icon: BarChart2, color: 'var(--rb-brand)', glow: 'rgba(205,164,46,0.12)' },
          { label: 'Saved Estimates',  value: saWaterEstimates.length, icon: Receipt,  color: '#f97316', glow: 'rgba(249,115,22,0.12)' },
        ].map(({ label, value, icon: Icon, color, glow }) => (
          <div key={label}
            className="relative overflow-hidden rounded-xl border border-slate-800/40 p-4 flex items-center gap-3"
            style={{ background: 'linear-gradient(135deg, #0d1527 0%, #080e1c 100%)', boxShadow: `0 0 0 1px rgba(255,255,255,0.04), 0 4px 20px ${glow}` }}>
            <div className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0 border"
              style={{ backgroundColor: `${color}15`, borderColor: `${color}30`, boxShadow: `0 0 16px ${color}20` }}>
              <Icon className="w-5 h-5" style={{ color }} />
            </div>
            <div className="min-w-0">
              <p className="text-2xl font-black text-white leading-none mb-0.5" style={{ textShadow: `0 0 20px ${color}40` }}>{value}</p>
              <p className="text-[11px] text-slate-500 truncate">{label}</p>
            </div>
            {/* Decorative glow blob */}
            <div className="absolute -right-4 -top-4 w-16 h-16 rounded-full opacity-20 blur-xl pointer-events-none"
              style={{ backgroundColor: color }} />
          </div>
        ))}
      </div>

      {/* Search bar */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-500" />
        <input
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder="Search trend reports by number, title, customer or site…"
          className="w-full bg-slate-800/60 border border-slate-800/50 rounded-xl pl-9 pr-9 py-2 text-xs text-white placeholder-slate-600 focus:outline-none focus:border-rb-brand/40"
        />
        {search && (
          <button onClick={() => setSearch('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-white">
            <X className="w-3.5 h-3.5" />
          </button>
        )}
      </div>

      {/* ═══════════════════════════════════════════════════════════════════════ */}
      {/* SECTION A — Effluent Trend Analysis                                   */}
      {/* ═══════════════════════════════════════════════════════════════════════ */}
      <div className="space-y-3">
        {/* Section header */}
        <button
          className="w-full flex items-center justify-between px-4 py-3.5 rounded-xl border border-rb-brand/20 hover:border-rb-brand/35 transition-all group"
          style={{ background: 'linear-gradient(90deg, rgba(205,164,46,0.08) 0%, rgba(8,14,28,0.9) 100%)' }}
          onClick={() => setSecAOpen(o => !o)}
        >
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg bg-rb-brand/15 border border-rb-brand/25 flex items-center justify-center"
              style={{ boxShadow: '0 0 12px rgba(205,164,46,0.2)' }}>
              <TrendingUp className="w-4 h-4 text-rb-brand" />
            </div>
            <div>
              <span className="text-[12px] font-black uppercase tracking-widest text-rb-brand">Section A</span>
              <span className="text-[12px] font-semibold text-slate-300 ml-2">Effluent Trend Analysis</span>
            </div>
            <span className="text-[11px] px-2 py-0.5 rounded-full font-bold border bg-rb-brand/15 text-rb-brand border-rb-brand/25">{effluentSamples.length} samples</span>
            {sectionASiteGroups.length > 0 && (
              <span className="text-[11px] px-2 py-0.5 rounded-full font-bold border bg-slate-800/80 text-slate-400 border-slate-800/50">{sectionASiteGroups.length} site{sectionASiteGroups.length !== 1 ? 's' : ''}</span>
            )}
          </div>
          <div className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg border text-[11px] font-bold transition-all
            ${secAOpen
              ? 'bg-rb-brand/10 text-rb-brand border-rb-brand/25'
              : 'bg-slate-800/60 text-slate-400 border-slate-800/50 group-hover:border-slate-600 group-hover:text-slate-300'}`}>
            {secAOpen ? <><ChevronDown className="w-3.5 h-3.5" />Collapse</> : <><ChevronRight className="w-3.5 h-3.5" />Open</>}
          </div>
        </button>

        <AnimatePresence>
          {secAOpen && (
            <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.2 }} className="overflow-hidden">
              <div className="space-y-3">

                {customerName && (
                  <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-teal-500/5 border border-teal-500/15 text-[10px] text-teal-400">
                    <Filter className="w-3 h-3 shrink-0" />
                    Customer filter active: <span className="font-bold ml-1">{customerName}</span>
                  </div>
                )}

                {/* Site-grouped sample cards */}
                {(loadingSamples || loadingSites) ? (
                  <div className="flex items-center justify-center py-10"><Loader2 className="w-5 h-5 text-slate-600 animate-spin" /></div>
                ) : sectionASiteGroups.length === 0 && effluentSamples.length === 0 ? (
                  <div className="flex flex-col items-center py-12 rounded-2xl border border-dashed border-rb-brand/20">
                    <Beaker className="w-10 h-10 text-rb-brand/30 mb-3" />
                    <p className="text-sm font-bold text-slate-500">No effluent samples loaded</p>
                    <p className="text-xs text-slate-600 mt-1 max-w-xs text-center">
                      {customerId ? 'No effluent samples found for this customer. Add samples via the Effluent Entry page.' : 'Select a customer to load their effluent samples.'}
                    </p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {sectionASiteGroups.map(g => (
                      <SiteTrendGroup key={g.key} site={g.site} samples={g.samples} isSAWater={g.isSAWater} />
                    ))}
                  </div>
                )}

                {/* Generated trend reports sub-section */}
                {filteredReports.length > 0 && (
                  <div className="space-y-2">
                    <div className="flex items-center gap-2 px-1 py-1">
                      <div className="w-5 h-5 rounded-md bg-rb-brand/15 border border-rb-brand/25 flex items-center justify-center">
                        <FileText className="w-3 h-3 text-rb-brand" />
                      </div>
                      <span className="text-[11px] font-bold text-slate-300">Generated Trend Reports</span>
                      <span className="text-[11px] px-1.5 py-0.5 rounded-full bg-rb-brand/10 text-rb-brand border border-rb-brand/20 font-bold">{filteredReports.length}</span>
                    </div>
                    {loadingReports ? (
                      <div className="flex items-center justify-center py-8"><Loader2 className="w-5 h-5 text-slate-600 animate-spin" /></div>
                    ) : (
                      filteredReports.map(r => {
                        const isExp = expandedId === r.id;
                        const paramKeys = Array.from(new Set(r.samples.flatMap(s => Object.keys(s.params || {})))).slice(0, 8);
                        return (
                          <motion.div
                            key={r.id}
                            initial={{ opacity: 0, y: 4 }} animate={{ opacity: 1, y: 0 }}
                            className={`relative rounded-xl border transition-all duration-200 overflow-hidden ${isExp ? 'border-rb-brand/40 bg-rb-surface/70' : 'border-slate-800/40 bg-rb-surface/60 hover:border-slate-500/50'}`}
                          >
                            <AnimatePresence>
                              {deleteConfirm === r.id && (
                                <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                                  className="absolute inset-0 z-20 bg-rb-elevated/96 rounded-xl flex items-center justify-center gap-3"
                                  onClick={e => e.stopPropagation()}>
                                  <span className="text-xs text-slate-300">Delete <strong className="text-white font-mono">{r.reportNumber}</strong>?</span>
                                  <button onClick={e => handleDelete(r, e)} className="px-3 py-1.5 rounded-lg text-xs font-bold bg-red-500/15 text-red-400 border border-red-500/30">Delete</button>
                                  <button onClick={e => { e.stopPropagation(); setDeleteConfirm(null); }} className="px-3 py-1.5 rounded-lg text-xs font-bold bg-slate-800 text-slate-400 border border-slate-700">Cancel</button>
                                </motion.div>
                              )}
                            </AnimatePresence>

                            <div className="flex items-center gap-3 px-4 py-3 cursor-pointer select-none" onClick={() => setExpandedId(prev => prev === r.id ? null : r.id)}>
                              <div className={`w-9 h-9 rounded-xl flex items-center justify-center shrink-0 ${isExp ? 'bg-rb-brand/15 border border-rb-brand/30' : 'bg-rb-brand/10 border border-rb-brand/15'}`}>
                                <Beaker className="w-4 h-4 text-rb-brand" />
                              </div>
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 mb-0.5 flex-wrap">
                                  <span className="text-[11px] font-mono text-rb-brand font-bold">{r.reportNumber}</span>
                                  <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-bold border ${r.source === 'server' ? 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20' : 'bg-amber-500/10 text-amber-400 border-amber-500/20'}`}>
                                    {r.source === 'server' ? '✦ DB' : '⬡ Local'}
                                  </span>
                                  <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-bold border ${stdBadgeClass(r.standard)}`}>{r.standard}</span>
                                  {r.siteName && (
                                    <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-slate-800/80 text-slate-400 border border-slate-800/50 flex items-center gap-0.5">
                                      <MapPin className="w-2 h-2" />{r.siteName}
                                    </span>
                                  )}
                                </div>
                                <p className="text-xs font-semibold text-white truncate">{r.reportTitle}</p>
                                <div className="flex items-center gap-3 mt-0.5 flex-wrap">
                                  {r.customerName && <span className="text-[10px] text-slate-500 flex items-center gap-1"><Building2 className="w-2.5 h-2.5" />{r.customerName}</span>}
                                  <span className="text-[10px] text-slate-500 flex items-center gap-1"><Layers className="w-2.5 h-2.5" />{r.samples.length} sample{r.samples.length !== 1 ? 's' : ''}</span>
                                  <span className="text-[10px] text-slate-500 flex items-center gap-1"><Calendar className="w-2.5 h-2.5" />{fmtDate(r.createdAt)}</span>
                                </div>
                              </div>
                              <div className="flex items-center gap-1.5 shrink-0" onClick={e => e.stopPropagation()}>
                                {r.source === 'local' && hasPhpBackend() && (
                                  <button onClick={e => handleSync(r, e)} disabled={syncingId === r.id}
                                    className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-[10px] font-semibold bg-amber-500/10 text-amber-400 border border-amber-500/20 hover:bg-amber-500/20 disabled:opacity-50">
                                    {syncingId === r.id ? <RefreshCw className="w-3 h-3 animate-spin" /> : <><Upload className="w-3 h-3" /> Sync</>}
                                  </button>
                                )}
                                <button onClick={e => handlePreview(r, e)} className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-[10px] font-semibold bg-slate-800/80 text-slate-300 border border-slate-800/60 hover:bg-slate-700 hover:text-white">
                                  <Eye className="w-3 h-3" /> Preview
                                </button>
                                <button onClick={e => handlePDF(r, e)} className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-[10px] font-semibold bg-rb-active/80 text-white border border-rb-brand/25 hover:bg-rb-active">
                                  <Download className="w-3 h-3 text-rb-brand" /> PDF
                                </button>
                                <button onClick={e => { e.stopPropagation(); setDeleteConfirm(r.id); }} className="p-1.5 rounded-lg text-slate-600 hover:text-red-400 hover:bg-red-500/10">
                                  <Trash2 className="w-3.5 h-3.5" />
                                </button>
                              </div>
                              <ChevronDown className={`w-4 h-4 text-slate-500 shrink-0 transition-transform duration-200 ${isExp ? 'rotate-180 text-rb-brand' : ''}`} />
                            </div>

                            <AnimatePresence>
                              {isExp && (
                                <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.2 }} className="overflow-hidden">
                                  <div className="border-t border-slate-800/50 px-4 pt-4 pb-5 space-y-4">
                                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                                      {[
                                        { label: 'Report Reference', value: r.reportNumber, cls: 'font-mono text-rb-brand break-all text-[11px] font-bold' },
                                        { label: 'Standard',         value: r.standard,     cls: 'text-white text-[11px] font-bold' },
                                        { label: 'Generated',        value: fmtDateFull(r.createdAt), cls: 'text-white text-[11px]' },
                                        { label: 'Storage',          value: r.source === 'server' ? '✦ Database' : '⬡ Local', cls: `text-[11px] font-bold ${r.source === 'server' ? 'text-emerald-400' : 'text-amber-400'}` },
                                      ].map(({ label, value, cls }) => (
                                        <div key={label} className="bg-slate-800/40 border border-slate-800/40 rounded-lg p-2.5">
                                          <p className="text-[10px] text-slate-500 uppercase tracking-wider mb-1 font-semibold">{label}</p>
                                          <p className={cls}>{value}</p>
                                        </div>
                                      ))}
                                    </div>

                                    {(r.customerName || r.siteName) && (
                                      <div className="bg-slate-800/30 border border-slate-800/40 rounded-lg p-3">
                                        <p className="text-[10px] text-slate-500 uppercase tracking-wider font-semibold mb-2">Site &amp; Permit Details</p>
                                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1.5">
                                          {r.customerName && <div className="flex items-center gap-2"><Building2 className="w-3 h-3 text-slate-500 shrink-0" /><span className="text-[11px] text-slate-300">{r.customerName}</span></div>}
                                          {r.siteName && <div className="flex items-center gap-2"><MapPin className="w-3 h-3 text-slate-500 shrink-0" /><span className="text-[11px] text-slate-300 font-bold">{r.siteName}</span></div>}
                                          {r.siteAddress && <div className="flex items-center gap-2 sm:col-span-2"><MapPin className="w-3 h-3 text-slate-600 shrink-0" /><span className="text-[11px] text-slate-400">{r.siteAddress}</span></div>}
                                          {r.sitePermitRef && <div className="flex items-center gap-2"><Hash className="w-3 h-3 text-slate-500 shrink-0" /><span className="text-[11px] text-slate-300">Permit: <span className="font-mono text-rb-brand">{r.sitePermitRef}</span></span></div>}
                                          {r.siteJurisdiction && <div className="flex items-center gap-2"><Shield className="w-3 h-3 text-slate-500 shrink-0" /><span className="text-[11px] text-slate-300">{r.siteJurisdiction}</span></div>}
                                        </div>
                                      </div>
                                    )}

                                    {r.samples.length > 0 && (
                                      <div>
                                        <p className="text-[10px] text-slate-500 uppercase tracking-wider font-semibold mb-2">
                                          Effluent Sample Data ({r.samples.length} sample{r.samples.length !== 1 ? 's' : ''})
                                        </p>
                                        <div className="overflow-x-auto rounded-lg border border-slate-800/40">
                                          <table className="w-full text-[11px]">
                                            <thead>
                                              <tr className="bg-slate-800/60 border-b border-slate-800/40">
                                                <th className="text-left px-3 py-2 text-slate-400 font-semibold whitespace-nowrap">Seq</th>
                                                <th className="text-left px-3 py-2 text-slate-400 font-semibold min-w-[160px]">Sample Name / ID</th>
                                                <th className="text-left px-3 py-2 text-slate-400 font-semibold whitespace-nowrap">Date</th>
                                                {paramKeys.map(k => (
                                                  <th key={k} className="text-right px-2 py-2 text-slate-400 font-semibold whitespace-nowrap">
                                                    {PARAM_LABELS[k] || k.toUpperCase()}{PARAM_UNITS[k] ? ` (${PARAM_UNITS[k]})` : ''}
                                                  </th>
                                                ))}
                                              </tr>
                                            </thead>
                                            <tbody>
                                              {r.samples.map((s, i) => (
                                                <tr key={i} className={`border-b border-slate-800/50 ${i % 2 === 0 ? 'bg-slate-800/20' : ''}`}>
                                                  <td className="px-3 py-2 text-slate-500 font-mono font-bold text-[10px]">S-{String(i+1).padStart(3,'0')}</td>
                                                  <td className="px-3 py-2 text-slate-200 font-medium">{s.name}</td>
                                                  <td className="px-3 py-2 text-slate-400 whitespace-nowrap">{fmtDate(s.date)}</td>
                                                  {paramKeys.map(k => {
                                                    const val = s.params[k];
                                                    if (typeof val !== 'number' || val <= 0) return <td key={k} className="px-2 py-2 text-right text-slate-600">—</td>;
                                                    const colour = paramColour(k, val, r.standard);
                                                    return (
                                                      <td key={k} className="px-2 py-2 text-right">
                                                        <span className={`inline-block px-1.5 py-0.5 rounded text-[10px] font-bold border ${COLOUR_CLASSES[colour]}`}>
                                                          {val.toFixed(k === 'ph' ? 2 : 1)}
                                                        </span>
                                                      </td>
                                                    );
                                                  })}
                                                </tr>
                                              ))}
                                            </tbody>
                                          </table>
                                        </div>
                                        {r.samples.length > 1 && paramKeys.slice(0,4).length > 0 && (
                                          <div className="mt-3 p-3 bg-slate-800/30 rounded-lg border border-slate-800/30">
                                            <p className="text-[10px] text-slate-500 uppercase tracking-wider font-semibold mb-2 flex items-center gap-1">
                                              <BarChart3 className="w-3 h-3" /> Parameter Trend
                                            </p>
                                            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                                              {paramKeys.slice(0,4).map(k => {
                                                const stdObj = ALL_STANDARDS.find(s => s.code === r.standard);
                                                const limit  = stdObj ? (stdObj.limits as Record<string, unknown>)[k.toLowerCase()] : undefined;
                                                const values = r.samples.map(s => typeof s.params[k] === 'number' ? s.params[k] as number : 0).filter(v => v > 0);
                                                if (values.length < 2) return null;
                                                const avg    = values.reduce((a, b) => a + b, 0) / values.length;
                                                const colour = paramColour(k, avg, r.standard);
                                                const colCls = { red: 'text-red-400', amber: 'text-amber-400', green: 'text-emerald-400', neutral: 'text-slate-400' }[colour];
                                                return (
                                                  <div key={k} className="space-y-1">
                                                    <div className="flex items-center justify-between">
                                                      <span className="text-[10px] font-bold text-slate-400">{PARAM_LABELS[k] || k.toUpperCase()}</span>
                                                      <span className={`text-[10px] font-bold ${colCls}`}>avg {avg.toFixed(1)}</span>
                                                    </div>
                                                    <Sparkline values={values} limit={typeof limit === 'number' ? limit : undefined} />
                                                    {typeof limit === 'number' && (
                                                      <p className="text-[10px] text-slate-600">Limit: {limit}{PARAM_UNITS[k] ? ' ' + PARAM_UNITS[k] : ''}</p>
                                                    )}
                                                  </div>
                                                );
                                              })}
                                            </div>
                                          </div>
                                        )}
                                      </div>
                                    )}

                                    <div className="flex items-center gap-2 pt-1 border-t border-slate-800/30">
                                      <button onClick={e => handlePreview(r, e)} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[11px] font-semibold bg-slate-800 text-slate-300 border border-slate-700 hover:text-white hover:bg-slate-700">
                                        <Eye className="w-3.5 h-3.5" /> Preview
                                      </button>
                                      <button onClick={e => handlePDF(r, e)} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[11px] font-semibold bg-rb-active text-white border border-rb-brand/30 hover:bg-rb-active/80">
                                        <Download className="w-3.5 h-3.5 text-rb-brand" /> Download PDF
                                      </button>
                                      <button onClick={e => { e.stopPropagation(); setDeleteConfirm(r.id); }} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[11px] font-semibold text-slate-600 border border-slate-800/50 hover:text-red-400 hover:bg-red-500/10 hover:border-red-500/20 ml-auto">
                                        <Trash2 className="w-3.5 h-3.5" /> Delete
                                      </button>
                                    </div>
                                  </div>
                                </motion.div>
                              )}
                            </AnimatePresence>
                          </motion.div>
                        );
                      })
                    )}
                  </div>
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* ═══════════════════════════════════════════════════════════════════════ */}
      {/* SECTION B — SA Water Trade Waste                                      */}
      {/* ═══════════════════════════════════════════════════════════════════════ */}
      <div className="space-y-3">
        {/* Section header */}
        <button
          className="w-full flex items-center justify-between px-4 py-3.5 rounded-xl border border-orange-500/20 hover:border-orange-500/35 transition-all group"
          style={{ background: 'linear-gradient(90deg, rgba(249,115,22,0.08) 0%, rgba(8,14,28,0.9) 100%)' }}
          onClick={() => setSecBOpen(o => !o)}
        >
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg bg-orange-500/15 border border-orange-500/25 flex items-center justify-center"
              style={{ boxShadow: '0 0 12px rgba(249,115,22,0.2)' }}>
              <Receipt className="w-4 h-4 text-orange-400" />
            </div>
            <div>
              <span className="text-[12px] font-black uppercase tracking-widest text-orange-400">Section B</span>
              <span className="text-[12px] font-semibold text-slate-300 ml-2">SA Water Trade Waste</span>
            </div>
            <span className="text-[11px] px-2 py-0.5 rounded-full font-bold border bg-orange-500/15 text-orange-400 border-orange-500/25">{saWaterEstimates.length} estimates</span>
            {saSites.length > 0 && (
              <span className="text-[11px] px-2 py-0.5 rounded-full font-bold border bg-slate-800/80 text-slate-400 border-slate-800/50">{saSites.length} SA Water site{saSites.length !== 1 ? 's' : ''}</span>
            )}
          </div>
          <div className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg border text-[11px] font-bold transition-all
            ${secBOpen
              ? 'bg-orange-500/10 text-orange-400 border-orange-500/25'
              : 'bg-slate-800/60 text-slate-400 border-slate-800/50 group-hover:border-slate-600 group-hover:text-slate-300'}`}>
            {secBOpen ? <><ChevronDown className="w-3.5 h-3.5" />Collapse</> : <><ChevronRight className="w-3.5 h-3.5" />Open</>}
          </div>
        </button>

        <AnimatePresence>
          {secBOpen && (
            <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.2 }} className="overflow-hidden">
              <div className="space-y-3">
                {!customerId ? (
                  <div className="flex flex-col items-center justify-center py-14 rounded-2xl border border-dashed border-orange-500/20" style={{ background: 'rgba(249,115,22,0.02)' }}>
                    <Receipt className="w-10 h-10 text-orange-500/30 mb-3" />
                    <p className="text-sm font-bold text-slate-500">No active customer session</p>
                    <p className="text-xs text-slate-600 mt-1 mb-4 max-w-xs text-center">Set an active customer in the Customers page to view their SA Water charge estimates.</p>
                    <button onClick={() => navigate('/customers')} className="flex items-center gap-1.5 px-4 py-2 rounded-xl text-[11px] font-bold bg-orange-500/10 text-orange-400 border border-orange-500/20 hover:bg-orange-500/20 transition-all">
                      <Building2 className="w-3.5 h-3.5" /> Go to Customers
                    </button>
                  </div>
                ) : loadingSites || loadingSamples ? (
                  <div className="flex items-center justify-center py-10"><Loader2 className="w-5 h-5 text-slate-600 animate-spin" /></div>
                ) : saSites.length === 0 ? (
                  <div className="flex flex-col items-center py-10 rounded-2xl border border-dashed border-orange-500/15">
                    <Droplets className="w-9 h-9 text-orange-500/25 mb-3" />
                    <p className="text-sm font-bold text-slate-500">No SA Water sites for {customerName}</p>
                    <p className="text-xs text-slate-600 mt-1 max-w-xs text-center">Add sites with the SA_WATER standard in the Customers page.</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    <p className="text-[10px] text-slate-600 px-1">
                      {saSites.length} SA Water site{saSites.length !== 1 ? 's' : ''} — {saWaterEstimates.length} total estimate{saWaterEstimates.length !== 1 ? 's' : ''}
                    </p>
                    {saSites.map(site => (
                      <SAWaterSiteSection
                        key={site.id}
                        site={site}
                        samples={samplesBySite.get(String(site.id)) || []}
                        estimates={estimatesBySite.get(site.id) || []}
                        customerName={customerName}
                        preparedBy={preparedBy}
                        onEstimateSaved={loadEstimates}
                      />
                    ))}
                  </div>
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

    </div>
  );
}
